/*
	historyManager sets up a listener to capture the hash changes in the URL 
	i.e. the tab hash is included in the URL and when the user navigates through the wizard, the hash changes.
	When the hash changes, the tab is clicked. 
	Also, if the user navigates back and forth, the tab is clicked. (browser back/forward buttons)
	This will also ensure that when the window is loaded, the state in the browser's history is blank and the first tab is selected.
*/
function historyManager() {

	const tabsComponent = document.querySelector('wizard-tabs-component');

	window.addEventListener('popstate', (e)=> {
		let currentHash = window.location.hash;	
		tabsComponent.querySelector(`.aTab[href="${currentHash}"]`).click();
	});

	window.addEventListener('load', function(e) {
		history.replaceState("", "", `${location.pathname}#food-planner-step-1`);
	});

}
historyManager();

/* 
	Wizard Tabs component. Responsible mainly for the tab switching functionality. 
	See: https://inclusive-components.design/tabbed-interfaces/
*/
class WizardTabsComponent extends HTMLElement {


	constructor() {
		super();
		this.tabbed = this; 
		this.tabBtnWrapper = this.tabbed.querySelector('ul.tablist');
		this.tablist = this.tabbed.querySelector('ul'); 
		this.tabs = this.tablist.querySelectorAll('a.aTab');
		this.panels = this.tabbed.querySelectorAll('[id^="food-planner-step"]'); 
		this.initTabs();
		this.addTabListeners();     
	}


	/*
		initTabs method initializes the tabs and panels, setting attributes and roles.
		This includes: 
			- adding role="tablist" to the tablist, role="tabpanel" to the panels,
			- setting aria-labelledby to the panels, (setting up ids for the tabs and panels)
			- selecting the first panel by setting aria-selected,
	*/
	initTabs() {
		this.tablist.setAttribute('role', 'tablist');

		Array.prototype.forEach.call(this.panels, (panel, i) => {
			panel.setAttribute('role', 'tabpanel');
			panel.setAttribute('tabindex', '-1');
			panel.setAttribute('aria-labelledby', `tab` + (i + 1));
			panel.hidden = true;                 
		});

		Array.prototype.forEach.call(this.tabs, (tab, i) => {
			tab.setAttribute('role', 'tab');
			tab.setAttribute('id', `tab` + (i + 1));
			tab.setAttribute('tabindex', '-1');
			tab.parentNode.setAttribute('role', 'presentation');   
		});

		if(this.tabs[0]) {
			this.tabs[0].removeAttribute('tabindex');
			this.tabs[0].setAttribute('aria-selected', 'true'); 
		}

		if(this.panels[0]) { this.panels[0].hidden = false }      
		//if (window.matchMedia('(max-width: 47.938em)').matches) { this.turnToAccordion() }

	}

	/*
		addTabListeners method adds event listeners to the tabs (click and keyboard).
		e.which === 37: left arrow key (key code 37)
		e.which === 39: right arrow key (key code 39)
		When a tab is clicked, the switchTab method is called.
		When a tab is focused and the left or right arrow key is pressed, the switchTab method is called.
	*/
	addTabListeners() {

		Array.prototype.forEach.call(this.tabs, (tab, i) => {

			tab.addEventListener('click', e => {
				//e.preventDefault();
				let currentTab = this.tablist.querySelector('[aria-selected]');
				if (e.currentTarget !== currentTab) {
					this.switchTab(currentTab, e.currentTarget);
				}
			});

			tab.addEventListener('keydown', e => {

				let index = Array.prototype.indexOf.call(this.tabs, e.currentTarget);
				let dir = e.which === 37 ? index - 1 : e.which === 39 ? index + 1 : null;

				if (dir !== null) {
					e.preventDefault();			
					let newIndex;
					if (this.tabs[dir]) { 
						newIndex = dir; 
					} else { 
						newIndex = dir === index - 1 ? this.tabs.length - 1 : 0; 
					}
					this.switchTab(e.currentTarget, this.tabs[newIndex]);
					this.tabs[newIndex].focus();
				}
			});

		});

	}  

	/*
		switchTab is responsible for switching the tab and the panel.
		It sets the new tab as focused, removes tabindex from the old tab, sets aria-selected to true for the new tab,
		removes aria-selected from the old tab, sets tabindex to -1 for the old tab,
		hides the old panel and shows the new panel.
	*/
	switchTab(oldTab, newTab) {
		newTab.focus();
		newTab.removeAttribute('tabindex');
		newTab.setAttribute('aria-selected', 'true');
		oldTab.removeAttribute('aria-selected');
		oldTab.setAttribute('tabindex', '-1');
		let index = Array.prototype.indexOf.call(this.tabs, newTab);
		let oldIndex = Array.prototype.indexOf.call(this.tabs, oldTab);
		this.panels[oldIndex].hidden = true;
		this.panels[index].hidden = false;
	}


}
customElements.define('wizard-tabs-component', WizardTabsComponent);
  


/*
	Found in Step 5, when the user has chosen the recipes, the treats section appears.
	It is responsible for switching between the treats panels.
	It is a simplified version of the WizardTabsComponent.

	FUTURE: 
		Can be refactored to use the WizardTabsComponent.
		This might cause some issues with the current implementation of the WizardTabsComponent.
		This is because there will be two instances of the same component nested in each other.
*/
class CollectionToggler extends HTMLElement {

	constructor() {
		super();
		this.buttons = this.querySelectorAll('.content-toggler');
		this.contents = this.querySelectorAll('.a-collection');
		this.addListeners();
	}

	addListeners() {
		this.buttons.forEach(btn => {
			btn.addEventListener('click', ()=> {
				
				let controlTarget = btn.getAttribute('aria-controls');
				this.contents.forEach(cnt => {
					
					this.buttons.forEach(btn => {
						btn.setAttribute('aria-expanded', false);
						cnt.classList.remove('flex');
						cnt.classList.add('none');
					})

					setTimeout(() => {
						if(cnt.id == controlTarget) {
							btn.setAttribute('aria-expanded', true);
							cnt.classList.remove('none');
							cnt.classList.add('flex');
						} 
					})

				})

			})
		})
	}



}
customElements.define('collection-toggler', CollectionToggler)



/*
	Found in Step 5, when the user has chosen recipes, the treats section appears.
	This is the treat product card. Each <product-card> carries the data a set of data-attributes.
	These include:
		- item_id
		- item_price
		- item_compareprice
		- item_policy
		- item_totalprice
		- item_totalqty
		- item_totalcompareprice (this might not be needed in the Planner, check if it can be removed)
		- item_variant_invqty
	These data attributed are initialized by product.selected_or_first_available_variant.xxxx in step 5
	and are updated accordingly when the user interacts with the card.
	
	Each card also includes:
		- a quantity input field, 
		- a plus and a minus button to increase or decrease the quantity,
		- a select field for the variants (if any)
*/
class ProductCard extends HTMLElement {
  

	constructor() {
		
		super();

		this.item_ID = Number(this.dataset.item_id)
		this.item_Price = Number(this.dataset.item_price); 	     
		this.item_TotalQTY = Number(this.dataset.item_totalqty);
		this.item_Policy = this.dataset.item_policy;
		this.item_TotalPrice = Number(this.dataset.item_totalprice);

		this.btn_plus = this.querySelector('input.plusQty');
		this.btn_minus = this.querySelector('input.minusQty');

		this.label_itemTotalQTY = this.querySelector('.label-item-total-qty'); 
		this.label_itemPricePerItem = this.querySelector('.label-price-per-item');

		this.variant_selects = this.querySelectorAll('.variant-select select');
		if(this.variant_selects.length !== 0) { this.listeners_variants() }

		this.listeners_quantity();
		
	}


	/*
		listeners_variants method adds event listeners to the variant select field.
		When a variant is selected, the card data is updated. 
		The data being updated come from the <option>s and include:
			- item_id (because each variant has own unique variant ID)
			- item_price (because each variant might have different price)
			- item_policy (because each variant might have selling policy)	
			- item_totalprice
			- item_totalqty

		Also, when the user selects a variant, the plus and minus buttons must be checked to see if they should be enabled or disabled.
		This can also affect the quantity of the item, in cases where the user first changes the quantity of the item, then selects a variant with a lower inventory quantity.
	*/
	listeners_variants() {
		this.variant_selects.forEach(select => {
			this.update_card_data(select);
			select.addEventListener('change', ()=> {			
				this.update_card_data(select);
				this.control_check_minusBtn(select)
				this.control_check_plusBtn(select)
			})

		})
	}


	/*
		listeners_quantity method adds event listeners to the plus and minus buttons.
		When the minus button / plus button is clicked, the quantity is adjusted.
		We need to verify if the plus and minus buttons should be enabled or disabled, depending on the variant quantity.
		The updated labels are the total quantity of the variant the user has selected.
		When the quantity is increased or decreased, the quantity is checked to see if it is more than the inventory quantity.
		If the quantity is more than the inventory quantity, the quantity is set to the inventory quantity. (so current max qty)
	*/
	listeners_quantity() {

		this.btn_minus.addEventListener('click', ()=> { 
			this.qty_decrease();       
			this.control_check_minusBtn();
			this.labels_update();
		})
		
		this.btn_plus.addEventListener('click', ()=> {             
			this.qty_increase(); 
			this.control_check_plusBtn();
			this.labels_update();
		})

	}


	/*
		update_card_data method updates the card data when a variant is selected.
		So each <option> within the select carries the data of the variant.
	*/
	update_card_data(select) {	
		this.current_selectedOption = select.options[ select.selectedIndex ];
		this.item_Price = this.current_selectedOption.dataset.variant_price;		
		this.dataset.item_id = this.current_selectedOption.value;	
		this.dataset.item_price = this.current_selectedOption.dataset.variant_price;
		this.dataset.item_policy = this.current_selectedOption.dataset.variant_policy;
		this.dataset.item_totalprice = `${ (this.item_TotalQTY * this.item_Price) }`;
		this.label_itemPricePerItem.textContent = `${this.current_selectedOption.dataset.variant_pricemoney}`;	
		this.setAttribute('data-item_variant_invqty', `${this.current_selectedOption.dataset.variant_invqty}`);
	}


	qty_increase() {
		this.item_TotalQTY = this.item_TotalQTY + 1   
		this.dataset.item_totalqty = `${this.item_TotalQTY}`;         
		this.dataset.item_totalprice = `${ (this.item_TotalQTY * this.item_Price) }`;
	}


	qty_decrease() {
		this.item_TotalQTY = this.item_TotalQTY - 1;
		this.dataset.item_totalqty = `${this.item_TotalQTY}`;         
		this.dataset.item_totalprice = `${ (this.item_TotalQTY * this.item_Price) }`;
	}


	labels_update() {  
		this.label_itemTotalQTY.textContent = `${this.item_TotalQTY}`;
	}


	/*
		control_check_minusBtn method CHECKS the quantity of the item.
		If the quantity is 0, the minus button is disabled.
		If the quantity is more than 0, the minus button is enabled.
		If the quantity is more than the inventory quantity, the quantity is set to the inventory quantity. (so current max qty)
	*/
	control_check_minusBtn(select) {

		if(this.item_TotalQTY > 0){

			this.control_toggle('enable', this.btn_minus)
		
			if(this.variant_selects.length !== 0) {

				if(this.item_TotalQTY < Number(this.dataset.item_variant_invqty)){			
					this.control_toggle('enable', this.btn_plus)
				} 
				
				if(this.item_TotalQTY == Number(this.dataset.item_variant_invqty)) {		
					this.control_toggle('disable', this.btn_plus)
				} 
				
				if(this.item_TotalQTY > Number(this.dataset.item_variant_invqty)){
					this.item_TotalQTY = Number(this.dataset.item_variant_invqty);
					this.dataset.item_totalqty = this.item_TotalQTY;					
					this.update_card_data(select)
					this.labels_update()
				}

			}

		} else {	
			this.control_toggle('disable', this.btn_minus)		
			this.control_toggle('enable', this.btn_plus)
		}
	}


	/*
		Similar to control_check_minusBtn, but for the plus button. There is an additional check for the item policy.
		If the item policy is 'deny' and the quantity reaches max qty, the plus button is disabled.
	*/
	control_check_plusBtn() {

		if(this.item_TotalQTY > 0) {

			this.control_toggle('enable', this.btn_minus);
			this.control_toggle('enable', this.btn_plus);

			if(this.item_Policy == 'deny') {

				if(this.variant_selects.length !== 0) {
					this.item_TotalQTY < Number(this.dataset.item_variant_invqty) ? 
						this.control_toggle('enable', this.btn_plus) : 
						this.control_toggle('disable', this.btn_plus);
				} else {
					this.item_TotalQTY < Number(this.dataset.item_product_invqty) ? 
						this.control_toggle('enable', this.btn_plus) : 
						this.control_toggle('disable', this.btn_plus);
				}

			} 

		} else {			
			this.control_toggle('disable', this.btn_minus)
			this.control_toggle('enable', this.btn_plus)
		}

	}


	control_toggle(state, element) {         
		state == 'disable' ? element.disabled = true : element.disabled = false;
	}

  
}
customElements.define('product-card', ProductCard)
  
  
  
/*
	This component wraps the <collection-toggler> and <product-card> components.
	It is responsible for capturing the total quantity and total price of the treats.
	This is achieved with a MutationObserver that 'listens' to the <product-card> components and their data-attributes.
	When a change in a <product-card> is detected, the total quantity and total price are updated.
*/
class ExtrasManager extends HTMLElement {


	constructor() {
		
		super();

		this.items = this.querySelectorAll('product-card.a-treat'); 
		this.total_extras = this.dataset.total_extras;
		this.total_extras_price = this.dataset.total_extras_price;
		this.label_extras_total = this.querySelector('.label_extras_total_price');

		this.totalQTY = 0;
		this.totalPrice = 0;

		this.add_mutation_observer();
		this.calculate_bulk_totals();
		//this.update_labels_itemsPerCollection();

	}
	

	/*
		This is where the main thing happens. add_mutation_observer method adds a MutationObserver to the ExtrasManager component
		and captures any change happening to <product-card> components. (These changes occur when the user interacts with the cards, selects variant, changes quantity, etc.)
		When a change in the data-attributes of a <product-card> is detected, the calculate_bulk_totals and update_labels_itemsPerCollection methods are called.
	*/
	add_mutation_observer() {
		let targetNodes = document.querySelectorAll('product-card.a-treat');          
		let options = { attributes: true };         
		let callback = (mutationList, observer) => {
			for (const mutation of mutationList) {
				if (mutation.type === 'attributes') {
					if(mutation.attributeName === "data-item_totalprice") {
						this.calculate_bulk_totals();
						this.update_labels_itemsPerCollection();
					}
			                
				}              
			}
		};

		let buldMutationObserver = new MutationObserver(callback);
		targetNodes.forEach((targetNode) => {
			buldMutationObserver.observe(targetNode, options);
		})
	}


	/*
		calculate_bulk_totals method calculates the total quantity and total price of the treats.
		These values are then set as data-attributes to the ExtrasManager component.
		This updates the EXTRAS TOTAL (.label_extras_total_price, eg: Σύνολο από extra: xx,xx € )
		This also updated the FINAL TOTAL text and data-attributes (.finalTotal, eg: Σύνολο: xx,xx € )
	*/
	calculate_bulk_totals() {

		this.totalQTY = 0;
		this.totalPrice = 0;

		this.items.forEach((item) => {

			this.totalQTY = this.totalQTY + Number(item.dataset.item_totalqty);
			this.setAttribute('data-total_extras', `${this.totalQTY}`);

			this.totalPrice = this.totalPrice + Number(item.dataset.item_totalprice); 
			this.setAttribute('data-total_extras_price', `${this.totalPrice}`); 
			
			document.querySelector('.finalTotal').setAttribute('data-extras_total', `${this.totalPrice}`)  
							
		});

		// setTimeout(() => {
			let recipe_total = Number(document.querySelector('.finalTotal').dataset.recipe_total);
			let extras_total = Number(document.querySelector('.finalTotal').dataset.extras_total);
			let total = (recipe_total + extras_total) / 100;
			document.querySelector('.finalTotal').setAttribute('data-total_total', `${recipe_total + extras_total}`);
			document.querySelector('.finalTotal .label_combined_total').textContent = `${new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(total)}`;
			let temp_extras_total = extras_total / 100;
			document.querySelector('.label_extras_total_price').textContent = `${ new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(temp_extras_total)}`;					
		// }) 

		Number(this.dataset.total_extras_price) > 0 ? this.setAttribute('data-hasextras', 'true') : this.setAttribute('data-hasextras', 'false')

	}

	
	/*
		update_labels_itemsPerCollection method updates the labels (.label_itemsInSameCollection_total) of the <collection-toggler> component.
		These labels show the total quantity of items in that particular collection.
	*/
	update_labels_itemsPerCollection() {

		let labels = this.querySelectorAll('.label_itemsInSameCollection_total');
		if(labels.length !== 0) {
			labels.forEach(el => { el.remove() })
		}

		this.collections = this.querySelectorAll('.a-collection');
		this.collections.forEach(col => {

			let col_items = col.querySelectorAll('product-card');
			let itemsInSameCollection_total = 0;

			col_items.forEach(ci => {
				ci.classList.add(`in-${col.id}`);
				itemsInSameCollection_total = itemsInSameCollection_total + Number(ci.dataset.item_totalqty);
			})

			if(itemsInSameCollection_total > 0) {
				let col_button = this.querySelector(`button[aria-controls="${col.id}"]`);
				let label_itemsInSameCollection_total = document.createElement('span');
				label_itemsInSameCollection_total.style.marginRight = '1rem';
				label_itemsInSameCollection_total.classList.add('label_itemsInSameCollection_total');
				label_itemsInSameCollection_total.textContent = `(${itemsInSameCollection_total})`;
				col_button.insertBefore(label_itemsInSameCollection_total, col_button.querySelector('span'));
			}

		})

	}


}
customElements.define('extras-manager', ExtrasManager)
  
  

class FoodPlannerWizard extends HTMLElement {


	constructor() {

		super(); 

		/* 
			Global variables, you can find this in main-planner-new.liquid below the discount-debugger.
			It includes the recipes product data and the discount code.
		*/
		this.globalVariables = new GlobalPlannerVariables();

		this.healthIssuesArray = [];
		this.productIDsArray = [];
		this.allergiesArray = [];
		this.recipesArray = [];
		this.finalProduct = {};

		// this should contain final recipe prds + treats
		this.finalProductArray = [];

		// these come from the global variables
		// this.variantsArray = this.globalVariables.product_variants_capture; // No longer needed in new system 
		this.discount_code = this.globalVariables.discount_code;
			
		this.cat = [
            { _petType: 'CAT' },
			{ _name: '' }, 
			{ _gender: 'FEMALE' }, 
			{ _neutered: 'NO' }, 
			{ _adulthood: 'ADULT' },
			{ _age: null },         
			{ _weight: 3 }, 
			{ _grams_per_day: 90 },
			{ _activity: 'LOW' }, 
			{ _bodytype: 'SLIM' }, 
			{ _allergies: 'NO' }, 
			{ _typeOfAllergies: [] }, 
			{ _extraAllergies: '' }, 
			{ _healthIssues: '' }, 
			{ _typeOfHealthIssues: [] }, 
			{ _recipes: [] },
			{ _periodicity: '4' },
			{ _typeOfPlan: 'SUBSCRIPTION' },
			{ _birthday_day: '' },
			{ _birthday_month: '' },
			{ _birthday_year: '' }
		];   
		
		// FIELDS
		this.form 		        = this.querySelector('.mealPlannerForm');
		this.catNameField       = this.querySelector('[name="CAT_NAME"]');
		this.catGenderFields    = this.querySelectorAll('[name="CAT_GENDER"]');
		this.catNeuteredFields  = this.querySelectorAll('[name="CAT_NEUTERED"]');
		
		this.catAdulthoodFields = this.querySelectorAll('[name="CAT_ADULTHOOD"]');
		this.catBirthday_day    = this.querySelector('[name="CAT_BIRTHDAY_DAY"]');
		this.catBirthday_month  = this.querySelector('[name="CAT_BIRTHDAY_MONTH"]');

		this.catWeightField     = this.querySelector('[name="CAT_WEIGHT"]');  
		this.catActivityFields  = this.querySelectorAll('[name="CAT_ACTIVITY"]');    
		this.catBodytypeFields  = this.querySelectorAll('[name="CAT_BODYTYPE"]');
		this.catAllergiesDeciderFields    = this.querySelectorAll('[name="CAT_ALLERGIES_DECIDER"]');            
		this.catHealthIssuesDeciderFields = this.querySelectorAll('[name="CAT_HEALTH_ISSUES_DECIDER"]');       

		// AGE 
		this.catKittenAgeWrapper = this.querySelector('.catAgeMonths');
		this.catAdultAgeWrapper = this.querySelector('.catAgeYears');
		this.catKittenAgeFields  = this.catKittenAgeWrapper.querySelector('[name="CAT_AGE_MONTHS"]');   
		this.catAdultAgeFields  = this.catAdultAgeWrapper.querySelector('[name="CAT_AGE_YEARS"]');
		this.ageSelects         = [this.catKittenAgeFields, this.catAdultAgeFields];

		// ALLERGIES GROUP
		this.catAllergiesWrapper      = this.querySelector('.catAllergies'); 
		this.catAllergiesFields       = this.catAllergiesWrapper.querySelectorAll('[name="CAT_ALLERGIES"]');        
		this.catExtraAllergiesWrapper = this.querySelector('.catExtraAllergies');    
		this.allergiesText            = '';        
		this.extraAllergiesField      = this.querySelector('[name="EXTRA_ALLERGIES_TEXT"]');      
		this.catHealthIssuesWrapper   = this.querySelector('.catHealthIssues'); 
		this.catHealthIssuesFields    = this.catHealthIssuesWrapper.querySelectorAll('[name="CAT_HEALTH_ISSUES"]');     

		// STEP FIELDS GROUPED PER STEP
		this.step1Fields = [this.catNameField, ...this.catGenderFields, ...this.catNeuteredFields]; 
		this.step2Fields = [...this.catAdulthoodFields, ...this.ageSelects];
		this.step3Fields = [this.catWeightField, ...this.catActivityFields, ...this.catBodytypeFields];  
		this.step4Fields = [...this.catAllergiesDeciderFields, ...this.catAllergiesFields, this.extraAllergiesField, ...this.catHealthIssuesDeciderFields, ...this.catHealthIssuesFields];

		// NEXT PAGE BTNS
		this.goto2 = this.querySelector('.goto_step2');
		this.goto3 = this.querySelector('.goto_step3');
		this.goto4 = this.querySelector('.goto_step4');
		this.goto5 = this.querySelector('.goto_step5');

		// TAB LIST ITEMS
		this.litab2 = this.querySelector('#tab-li-2');
		this.litab3 = this.querySelector('#tab-li-3');
		this.litab4 = this.querySelector('#tab-li-4');
		this.litab5 = this.querySelector('#tab-li-5');

		// VARIOUS LABELS THROUGHOUT THE MEAL PLANNER
		this.labels_Name = this.querySelectorAll('.label_name'); 
		this.labels_Days = this.querySelectorAll('.label_days'); 
		this.labels_Price = this.querySelectorAll('.label_price');                
		this.labels_Gender = this.querySelectorAll('.label_gender');
		this.labels_Gender2 = this.querySelectorAll('.label_gender_alt');               
		this.label_ageAdult = this.querySelector('.label_ageAdult');
		this.label_ageKitten = this.querySelector('.label_ageKitten');          
		this.labels_Adulthood = this.querySelectorAll('.label_adulthood');
		this.labels_Periodicity = this.querySelectorAll('.label_periodicity');
		this.labels_week = this.querySelectorAll('.label_week');
		this.labels_weeks = this.querySelectorAll('.label_weeks');
		this.labels_GramsPerDay = this.querySelectorAll('.label_gramsperday');
		this.labels_Price_per_day = this.querySelectorAll('.label_price_per_day');

		// FINAL COMBINED TOTAL
		this.final_total_wrapper = this.querySelector('.finalTotal');
		//this.recipe_total = this.final_total_wrapper.dataset.recipe_total;
		//this.extras_total = this.final_total_wrapper.dataset.extras_total;
		this.label_combined_total = this.final_total_wrapper.querySelector('.label_combined_total');    

		// LIVE REGIONS      
		this.activityRegionTemplate = this.querySelector('#activityRegionTemplate');
		this.liveRegionTemplate = this.querySelector('#liveRegionTemplate');

		// PANELS, THESE ARE ALL FOUND IN STEP 5
		this.loaderPanel  = this.querySelector('.loaderPanel');
		this.errorPanel   = this.querySelector('.errorPanel');
		this.abortPanel   = this.querySelector('.abortPanel');
		this.prefacePanel = this.querySelector('.prefacePanel'); 

		this.preCheckoutPanel = this.querySelector('.preCheckoutPanel')
		this.checkoutPanel    = this.querySelector('.checkoutPanel');

		this.unavailablePanel = this.querySelector('.unavailablePanel');
		this.periodicityPanel = this.querySelector('.periodicityPanel');
		this.planTypePanel    = this.querySelector('.plantypePanel');

		// PRODUCT PANELS, FOUND IN STEP 5
		this.productsPanel = this.querySelector('.products-panel');    

		// PRODUCTS
		this.currentCheckedPrdCnt = 0; 
		this.products = this.querySelectorAll('.a-product');
		// this.matchedProduct; // No longer needed in new system    

		// FLAGS
		this.abortFlag = false;
		this.unavailableFlag = false;
		this.reached5flag = false;
		
		// PLAN TYPE, UNDER RECIPES, STEP 5
		this.planTypeFields = this.querySelectorAll('[name="TYPE_OF_PLAN"]');
		this.planTypeValue = 'SUBSCRIPTION';
		this.planTypeDescription_s = this.querySelector('.plantypeDescription-subscription');
		this.planTypeDescription_o = this.querySelector('.plantypeDescription-onetime');

		// PERIODICITY, UNDER PLAN TYPE, STEP 5
		this.periodicityFields = this.querySelectorAll('[name="PERIODICITY"]');           
		this.periodicityValue = '4';	
		this.periodicityDays = 28;   

		// ADD TO CART BUTTON
		this.atc = this.checkoutPanel.querySelector('.atcBtn');

		// RUN
		this.disableFormSubmission(); // disable form submission ENTER key
		//this.cart_clear(); // clears cart on load

		// LISTENERS FOR FIELDS IN EACH STEP
		this.listeners_Step1(); 
		this.listeners_Step2(); 
		this.listeners_Step3(); 
		this.listeners_Step4(); 
		this.listeners_Step5();

		// This is used for kittens but i cant remember why we did it this way. Most probably there is a better way.
		// DO NOT DELETE, these are used in grams_per_day_kitten()		
		this.kilogroup_1  = [ [0], [1], [2], [3,4], [5] ];
		this.kilogroup_2  = [ [0], [1], [2], [3,4], [5] ];
		this.kilogroup_3  = [ [0], [1], [2], [3,4], [5] ];
		this.kilogroup_4  = [ [0], [1], [2], [3,4], [5,6] ];
		this.kilogroup_5  = [ [0], [1], [2], [3,4], [5,6] ];
		this.kilogroup_6  = [ [0], [1], [2], [3,4], [5,6,7] ];
		this.kilogroup_7  = [ [0], [1], [2], [3,4], [5,6,7] ];
		this.kilogroup_8  = [ [0], [1], [2], [3,4], [5,6,7] ];
		this.kilogroup_9  = [ [1], [2], [3], [4,5,6], [7,8,9] ];
		this.kilogroup_10 = [ [1], [2], [3], [4,5,6], [7,8,9] ];
		this.kilogroup_11 = [ [1], [2], [3], [4,5,6], [7,8,9] ];
		this.kilogroup_12 = [ [1], [2], [3], [4,5,6], [7,8,9] ];
			
		// ...
		this.fixed_grams = [65, 90, 130, 180, 260];

		// New properties for interactive recipe selection (cat version - 2 recipes max)
		this.recipeData = window.masterRecipeData || {};
		this.activeRecipeHandles = []; // Currently selected recipes
		this.maxRecipesAllowed = 2; // Cats get max 2 recipes vs 4 for dogs
		this.recipePortions = {}; // Stores current portions for each recipe
		this.totalRequiredPortions = 28; // Will be updated based on periodicity
		this.isManualMode = false;
		this.manualModeToggle = this.querySelector('#manualModeToggle');
		this.manualModeStatus = this.querySelector('#manualModeStatus');
		this.customPortionControls = this.querySelector('.custom-portion-controls');

	}


	/* ==========================================
	=== Read / Write helper methods =============  
	=== to main array (main array = this.cat) === 
	========================================== */  
	data_write(key, value) {     
		this.cat.forEach((entry, index) => {          
			for (const property in entry) {           
				if(property === key) {
					entry[key] = value;
					// Enable this when debugging
					// console.log(entry, index, this.cat);
				}
			}
		});
	}

	data_read(keys = Array) {
		let temp = []
		keys.forEach((key) => {      
			this.cat.forEach((entry) => { 
			for (const property in entry) {
				if(property === key) {
					// Enable this when debugging
					// console.log(entry[key]);
					temp.push(entry[key]);
				}
			}       
			})
		});	
		return temp;
	}


	/* ==================================
	=== Event listeners for each step === 
	================================== */ 
	/*
		Note that each field has a callback function that is called when the field is changed / interacted with.
		The callback is also called on load, in cases where the field is pre-filled. (Firefox)
	*/
	listeners_Step1() {

		// Name input 	
		window.addEventListener("load", () => { this.callback_name_input(this.catNameField); });  	         
		this.catNameField.addEventListener('keyup', e => { this.callback_name_input(this.catNameField); });
		this.catNameField.addEventListener('blur', e => { this.callback_name_input(this.catNameField); }); 

		// Gender inputs
		this.catGenderFields.forEach((genderField) => { 
			window.addEventListener("load", () => { this.callback_gender_radios(genderField); });         
			genderField.addEventListener('change', ()=> { this.callback_gender_radios(genderField); });
		});

		// Neutered inputs
		this.catNeuteredFields.forEach((neuterField) => {
			window.addEventListener("load", () => { this.callback_neutered_radios(neuterField); });  
			neuterField.addEventListener('change', ()=> { this.callback_neutered_radios(neuterField); });  
		});

		// Next page btn 
		this.goto2.addEventListener('click', (e) => {                           
			this.validation_stepFields(this.step1Fields, this.goto2) == true ? (         
				this.labels_write({ name : this.data_read(['_name']), gender : this.data_read(['_gender']) }),       
				this.validation_toggleTab(true, [this.litab2]),
				this.goto_page(this.litab2.querySelector('a'))
			) : ( 
				this.validation_toggleTab(false, [this.litab2, this.litab3, this.litab4, this.litab5]) 
			)
		});
		
		this.labels_write({ name : this.data_read(['_name']), gender : this.data_read(['_gender']) });
		
	}

	listeners_Step2() {

		// Adulthood inputs    
		this.catAdulthoodFields.forEach((adulthoodField) => { 
			window.addEventListener("load", () => { this.callback_adulthood_radios(adulthoodField); });  
			adulthoodField.addEventListener('change', (e)=> { this.callback_adulthood_radios(adulthoodField); });
		});

		// Age selectors
		this.ageSelects.forEach((ageSelect) => { 
			window.addEventListener("load", () => { this.callback_age_selects(ageSelect); });
			ageSelect.addEventListener('change', (e) => { this.callback_age_selects(ageSelect); });      
		});

		// Birthday (day) input
		window.addEventListener("load", () => { this.callback_birthday_day(this.catBirthday_day); });  
		this.catBirthday_day.addEventListener('change', (e)=> { this.callback_birthday_day(this.catBirthday_day); });

		// Birthday (month) input
		window.addEventListener("load", () => { this.callback_birthday_month(this.catBirthday_month); });  
		this.catBirthday_month.addEventListener('change', (e)=> { this.callback_birthday_month(this.catBirthday_month); });

		// Next page btn
		this.goto3.addEventListener('click', (e) => {                
			this.validation_stepFields(this.step2Fields, this.goto3) == true ? (
				this.validation_toggleTab(true, [this.litab3]),
				this.goto_page(this.litab3.querySelector('a'))                    
			) : this.validation_toggleTab(false, [this.litab3, this.litab4, this.litab5])                   
		});    

	}

	listeners_Step3() {

		// Weight select
		window.addEventListener("load", () => { this.callback_weight_select(this.catWeightField) });
		this.catWeightField.addEventListener('change', ()=> { 
			this.callback_weight_select(this.catWeightField);                 
		}); 

		// Activity fields
		this.catActivityFields.forEach((activityField) => {
			window.addEventListener("load", () => { this.callback_activity_radios(activityField) });
			activityField.addEventListener('change', () => { 
				this.callback_activity_radios(activityField);       
			});
		});  

		// Bodytype fields
		this.catBodytypeFields.forEach((bodytypeField) => {
			window.addEventListener("load", () => { this.callback_type_radios(bodytypeField) });
			bodytypeField.addEventListener('change', () => {  
				this.callback_type_radios(bodytypeField);
			});
		});

		// Next page btn 
		this.goto4.addEventListener('click', (e) => {   
			this.validation_stepFields(this.step3Fields, this.goto4) == true ? (
				this.validation_toggleTab(true, [this.litab4]),
				this.goto_page(this.litab4.querySelector('a')) 
			) : this.validation_toggleTab(false, [this.litab4, this.litab5]);
		});

	}

	listeners_Step4() {

		// Allergies (yes/no)
		this.catAllergiesDeciderFields.forEach((allergyChoice) => {
			window.addEventListener("load", () => { this.callback_allergies_decider_radios(allergyChoice); });  
			allergyChoice.addEventListener('change', ()=> { 
				this.callback_allergies_decider_radios(allergyChoice);
			});
		});  

		// Allergies checkboxes
		this.catAllergiesFields.forEach((allergyField) => {
			window.addEventListener("load", () => { this.callback_allergies_checkboxes(allergyField); }); 
			allergyField.addEventListener('change', (e) => { 
				this.callback_allergies_checkboxes(allergyField, e);
			});
		});

		// Extra allergies textarea
		window.addEventListener("load", () => { this.callback_extrallergies_textarea(this.extraAllergiesField); }); 
		this.extraAllergiesField.addEventListener('blur', ()=> { this.callback_extrallergies_textarea(this.extraAllergiesField); });   
		this.extraAllergiesField.addEventListener('keyup', ()=> { this.callback_extrallergies_textarea(this.extraAllergiesField); });

		// Health issues inputs (yes/no)	
		this.catHealthIssuesDeciderFields.forEach((healthIssueChoice) => {
			window.addEventListener("load", () => { this.callback_healthissues_decider_radios(healthIssueChoice); });
			healthIssueChoice.addEventListener('change', ()=> { this.callback_healthissues_decider_radios(healthIssueChoice); });
		}); 

		// Health issues checkboxes
		this.catHealthIssuesFields.forEach((healthIssue) => {
			window.addEventListener("load", () => { this.callback_issues_checkboxes(healthIssue); });
			healthIssue.addEventListener('change', (e) => { this.callback_issues_checkboxes(healthIssue, e); });
		}); 

		// Next page btn 
		/* 
			Note that only in this step, panels_set_step5 runs. 
			It is responsible for setting the panels in step 5.
			Read more in the method itself.
		*/
		this.goto5.addEventListener('click', (e) => {      

			this.validation_stepFields(this.step4Fields, this.goto5) == true ? ( 
			this.validation_toggleTab(true, [this.litab5]), 
			this.goto_page(this.litab5.querySelector('a')), 
		
			this.panels_set_step5(),
			this.labels_write()

			) : this.validation_toggleTab(false, [this.litab5]);             
		});

	}  

	listeners_Step5() {
		// Fixed pricing panel
		this.fixed_pricing_panel_IO();

		// Product details (<details>)
		document.addEventListener('mouseup', (e) => {
			this.callback_product_details(e);
		});

		// Plan type fields
		this.planTypeFields.forEach((planTypeField) => {
			window.addEventListener("load", () => { this.callback_plantype_radios(planTypeField) });
			planTypeField.addEventListener('change', ()=> {
				this.callback_plantype_radios(planTypeField);         
			});                                  
		});

		// Periodicity fields
		this.periodicityFields.forEach((periodicityField) => {
			periodicityField.addEventListener('change', ()=> {  
				this.callback_periodicity_radios(periodicityField); 
			});
		});

		// Quantity control buttons for +/- in active recipes
		document.addEventListener('click', (e) => {
			const button = e.target;
			if (button.classList.contains('button--plus') || button.classList.contains('button--minus')) {
				e.preventDefault();
				const productCard = button.closest('.a-product');
				if (!productCard) return;

				const productHandle = productCard.dataset.productHandle;
				const input = productCard.querySelector('.recipe-portion-input');
				
				if (!input || !this.activeRecipeHandles.includes(productHandle)) return;

				// Enter manual mode if not already
				if (!this.isManualMode) {
					this.toggleManualMode(true, true); // Enter manual mode, skip redistribution
				}

				let currentValue = parseInt(input.value, 10) || 0;
				let newValue = currentValue;

				if (button.classList.contains('button--plus')) {
					newValue = currentValue + 1;
				} else if (button.classList.contains('button--minus')) {
					newValue = Math.max(0, currentValue - 1);
				}

				this.updatePortionAndPrice(productHandle, newValue);
				this.checkManualTotalAndToggleATC(); // Update manual mode status
			}
		});

		// ATC button
		this.atc.addEventListener('click', () => {
			(async () => {        
				// Use the new cart structure
				const itemsForCart = [];
				
				this.activeRecipeHandles.forEach(productHandle => {
					const portions = this.recipePortions[productHandle] || 0;
					if (portions > 0) {
						const gramsPerDay = this.data_read(['_grams_per_day']);
						const variantKey = `${this.periodicityValue}-ebdomades_${gramsPerDay}gr-mera`;
						const variantData = this.recipeData[productHandle]?.variants?.[variantKey];
						
						if (variantData) {
							let sellingPlanId = null;
							if (this.planTypeValue === 'SUBSCRIPTION' && variantData.sellingPlanId) {
								// sellingPlanId is now an array: [1week, 2weeks, 4weeks]
								// Map periodicityValue to array index
								let sellingPlanIndex = 0;
								switch(this.periodicityValue) {
									case '1': sellingPlanIndex = 0; break;
									case '2': sellingPlanIndex = 1; break;
									case '4': sellingPlanIndex = 2; break;
									default: sellingPlanIndex = 2; // Default to 4 weeks
								}
								sellingPlanId = Array.isArray(variantData.sellingPlanId) 
									? variantData.sellingPlanId[sellingPlanIndex] 
									: variantData.sellingPlanId; // Fallback for old format
							}

							// Validate variant data
							if (!variantData.variantId) {
								console.error('Missing variantId for product:', productHandle);
								return;
							}

							const cartItem = {
								id: variantData.variantId,
								quantity: portions,
								properties: {
									_petType: `${this.data_read(['_petType'])[0]}`,
									Pet: `${this.data_read(['_name'])[0]}`,
									_periodicity: `${this.periodicityValue}`,
									_typeOfPlan: `${this.planTypeValue}`
								}
							};

							if (sellingPlanId) {
								// Ensure selling plan ID is a number
								cartItem.selling_plan = parseInt(sellingPlanId, 10);
							}

							console.log('Adding cart item:', cartItem);
							itemsForCart.push(cartItem);
						}
					}
				});
				
				// Add extras/treats to cart if any are selected
				if(typeof(document.querySelector('extras-manager')) != 'undefined' && document.querySelector('extras-manager') != null){
					if(document.querySelector('extras-manager').dataset.hasextras == 'true') {
						let trts = document.querySelectorAll('product-card.a-treat');				
						trts.forEach((trt) => {     
							if( Number(trt.dataset.item_totalqty) > 0 ) {
								let temp_trt = {
									id: Number(trt.dataset.item_id), 
									quantity: Number(trt.dataset.item_totalqty),
									properties: {
										Pet: `${this.data_read(['_name'])[0]}`
									}
								};
								itemsForCart.push(temp_trt);
							}
						});
					}
				}
				
				this.finalProductArray = itemsForCart;
				console.log('Final product array prepared:', this.finalProductArray);
				
				let addThese = await this.cart_addItems();          

				if(addThese.status == 200 || addThese.status == 201) {
					// Add delay before redirect to allow cart state to sync on server
					setTimeout(() => {
						location.href = `/cart`;
					}, 500);
				} else {         
					console.error('Cart add failed with status:', addThese.status, 'Error:', addThese.error);
					this.panels_toggle([
						[this.errorPanel, true], [this.abortPanel, false], [this.prefacePanel, false], [this.periodicityPanel, false], [this.preCheckoutPanel, false],
						[this.productsPanel, false], [this.unavailablePanel, false], [this.checkoutPanel, false], [this.loaderPanel, false]
					]);
				} 	
			})();  
		})    
	}


	/* ====================
	=== Input Callbacks === 
	==================== */

	// ~~~ step 1 ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

	callback_name_input(input) {
		this.data_write('_name', input.value)       
		input.value == '' ? ( 
			this.validation_toggleStyle(false, input.closest('.fieldWrapper')),
			this.validation_toggleTab(false, [this.litab2, this.litab3, this.litab4, this.litab5]),
			this.validation_toggleStepBtn(false, this.goto2)
		) : ( 
			this.validation_toggleStyle(true, input.closest('.fieldWrapper')),
			this.validation_toggleStepBtn(true, this.goto2)
		) 
	}

	callback_gender_radios(radio) {
		if(radio.checked == true) {
			this.data_write('_gender', radio.value),         
			this.validation_toggleTab(false, [this.litab2, this.litab3, this.litab4, this.litab5])
		}
	}

	callback_neutered_radios(radio) {
		if(radio.checked == true) {
			this.data_write('_neutered', radio.value);      
			radio.value == 'YES' ? this.data_write('_activity', 'STERILE') : ( 
				this.catActivityFields.forEach((activityField) => { 
					activityField.checked == true ? this.data_write('_activity', activityField.value) : null 
				})
			);        
			this.validation_toggleTab(false, [this.litab2, this.litab3, this.litab4, this.litab5])
		}     
	}

  	// ~~~ step 2 ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

	callback_adulthood_radios(radio) {
		if(radio.checked == true) {

			this.data_write('_birthday_year', '');
			this.data_write('_birthday_month', '');
			this.catBirthday_month.selectedIndex = 0;

			this.data_write('_adulthood', radio.value);         
			this.age_toggleSelectors(radio); 
			this.age_resetSelector(radio); 
			this.validation_toggleTab(false, [this.litab3, this.litab4, this.litab5]); 
			
			if(this.validation_stepFields(this.step2Fields, this.goto3) == true) {
				this.validation_toggleStepBtn(true, this.goto3)
			} else {
				this.validation_toggleTab(false, [this.litab3, this.litab4, this.litab5])
			}

		}     
	}

	callback_birthday_day(select) {		
		this.data_write('_birthday_day', select.value)
	}

	callback_birthday_month(select) {
		let selectedOption = null;
		for (var i = 0; i < select.options.length; i++) {
			if (select.options[i].value === select.value) {
				selectedOption = select.options[i];
				break;
			}
		}
		this.data_write('_birthday_month', `${select.value} (${selectedOption.textContent})`)
	}
  
	callback_age_selects(select) {
		if(select.value != '') {

			this.data_write('_age', select.value);
			this.data_write('_birthday_month', '');

			let selectedValue = Number(select.value);
			let currentYear = new Date().getFullYear();
			let currentMonth = new Date().getMonth() + 1; // Adding 1 to make it 1-indexed
			// Enable this when debugging
			// console.log('currentYear', currentYear, 'currentMonth', currentMonth)

			if(select == this.catAdultAgeFields) {

				this.data_write('_birthday_year', `${currentYear - selectedValue}`);

			} else {

				let birthYear, birthMonth, temp_month;
				let remainingMonths = selectedValue - currentMonth;
				// console.log('remainingMonths', remainingMonths)
			
				if (remainingMonths >= 0) {
					// If remainingMonths is positive or zero, the birth year is the current year minus 1
					// and the birth month must count from 12 minus remainingMonths
					birthYear = currentYear - 1;
					birthMonth = 12 - remainingMonths;
				} else {
					// If remainingMonths is negative, the birth year is the current year
					// and the birth month is the absolute value of remainingMonths
					birthYear = currentYear;
					birthMonth = currentMonth + remainingMonths;
				}

				for (let i = 0; i < this.catBirthday_month.options.length; i++) {
					if (Number(this.catBirthday_month.options[i].value) === birthMonth) {
						temp_month = this.catBirthday_month.options[i].textContent;
						this.catBirthday_month.options[i].selected = true;
						break;
					}
				}

				this.data_write('_birthday_year', `${birthYear}`);
				this.data_write('_birthday_month', `${birthMonth} (${temp_month})`);

			}

			//this.data_write('ageGroup', select.selectedOptions[0].dataset.agegroup);  
			this.labels_write({ _age : select.value });
			this.validation_toggleTab(false, [this.litab3, this.litab4, this.litab5]); 

			this.validation_stepFields(this.step2Fields, this.goto3) == true ? 
				this.validation_toggleStepBtn(true, this.goto3) : 
				this.validation_toggleTab(false, [this.litab3, this.litab4, this.litab5]);
			
		}
	}

  	// ~~~ step 3 ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  
	callback_weight_select(select) {

		if(select.value != '') {
			this.data_write('_weight', Number(select.value));

			// ok?
			if(this.data_read(['_adulthood']) == 'KITTEN') {
				this.data_write('_grams_per_day', this.grams_per_day_kitten() );          
			} else {  
				this.data_write('_grams_per_day', Number(select.selectedOptions[0].dataset.grperday) );
			}
			
			this.labels_write({ grams : select.value});
			this.validation_toggleTab(false, [this.litab4, this.litab5])

			if(this.validation_stepFields(this.step3Fields, this.goto4) == true) {
				this.validation_toggleStepBtn(true, this.goto4)
			} else {
				this.validation_toggleTab(false, [this.litab4, this.litab5])
			}

			//this.validation_stepFields(this.step3Fields, this.goto4) == true ? this.validation_toggleStepBtn(true, this.goto4) : this.validation_toggleTab(false, [this.litab4, this.litab5]) 
		}  

	}

	// callback_breed_input(input) {
	// 	this.data_write('breed', input.value) 
	// 	if(input.value == '') {
	// 		this.data_write('breed', 'UNKNOWN') 
	// 	}
	// }
  
	callback_activity_radios(radio) {
		if(radio.checked == true) {       
			this.data_read(['_neutered']) == 'YES' ? this.data_write('_activity', 'STERILE') : this.data_write('_activity', radio.value);                          
			this.a11y_updateActivityRegion(radio.dataset.desc); 
			this.validation_toggleTab(false, [this.litab4, this.litab5])

			if(this.validation_stepFields(this.step3Fields, this.goto4) == true) {
				this.validation_toggleStepBtn(true, this.goto4)
			} else {
				this.validation_toggleTab(false, [this.litab4, this.litab5])
			}	
		}
	}

	callback_type_radios(radio) {
		if(radio.checked == true) {

			if(this.data_read(['_neutered']) == 'YES') {
				this.data_write('_activity', 'STERILE')
			} else {
				if(radio.value == 'OBESE') { this.data_write('_activity', 'LOW') }
			}

			//this.data_read(['_neutered']) == 'YES' ? this.data_write('_activity', 'STERILE') : radio.value == 'OBESE' ? this.data_write('_activity', 'LOW') : null;

			this.data_write('_bodytype', radio.value); 
			this.validation_toggleTab(false, [this.litab4, this.litab5])

			if(this.validation_stepFields(this.step3Fields, this.goto4) == true) {
				this.validation_toggleStepBtn(true, this.goto4)
			} else {
				this.validation_toggleTab(false, [this.litab4, this.litab5])
			}

			//this.validation_stepFields(this.step3Fields, this.goto4) == true ? this.validation_toggleStepBtn(true, this.goto4) : this.validation_toggleTab(false, [this.litab4, this.litab5])  
		}    
	}

  	// ~~~ step 4 ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

	callback_allergies_decider_radios(radio) {
		if(radio.checked == true) {      
			this.data_write('_allergies', radio.value); 

			radio.value == 'NO' ? ( this.allergies_reset(), this.allergies_toggleAllergiesPanel(false) ) : this.allergies_toggleAllergiesPanel(true), 

			this.validation_stepFields(this.step4Fields, this.goto5) == true ? ( 
				this.data_write('_allergies', radio.value), 
				this.validation_toggleStepBtn(true, this.goto5),
				this.validation_toggleTab(false, [this.litab5])
			) : (
				this.validation_toggleStepBtn(false, this.goto5),
				this.validation_toggleTab(false, [this.litab5])
			)
		}
	}

	callback_allergies_checkboxes(checkbox, event) {
		checkbox.value == 'EXTRA_ALLERGY' ? (  
			checkbox.checked == true ? ( this.allergies_toggleAllergiesTextarea(true) ) : ( this.allergies_toggleAllergiesTextarea(false), this.data_write('_extraAllergies', ''), this.extraAllergiesField.value = '' )   
		) : (            
			checkbox.checked == true ? ( this.allergiesArray.push(checkbox.value), this.data_write('_typeOfAllergies', this.allergiesArray) ) : ( 
				this.allergiesArray = this.allergiesArray.filter(item => item !== checkbox.value), this.data_write('_typeOfAllergies', this.allergiesArray) 
			)           
		)  

		if(event != undefined) {
			this.toggleCheckBoxRequired(this.catAllergiesFields);     
			
			// If we're already on step 5, update the filtering immediately
			const step5Element = this.querySelector('#food-planner-step-5');
			if (step5Element && !step5Element.classList.contains('none')) {
				this.applyRecipeFilters();
				this.renderRecipeCardsState();
			}
			
			this.validation_stepFields(this.step4Fields, this.goto5) == true ? (
				this.validation_toggleStepBtn(true, this.goto5), this.validation_toggleTab(false, [this.litab5])
			) : (
				this.validation_toggleStepBtn(false, this.goto5), this.validation_toggleTab(false, [this.litab5])
			)
		}
	}

	callback_extrallergies_textarea(textarea) {
		this.allergiesText = textarea.value; 
		textarea.value == '' ? ( 
			this.data_write('_extraAllergies', ''), this.validation_toggleStyle(false, textarea)
		) : ( 
			this.data_write('_extraAllergies', textarea.value), this.validation_toggleStyle(true, textarea) 
		); 

		this.validation_stepFields(this.step4Fields, this.goto5) == true ? (
			this.validation_toggleStepBtn(true, this.goto5), this.validation_toggleTab(false, [this.litab5])
		) : (
			this.validation_toggleStepBtn(false, this.goto5), this.validation_toggleTab(false, [this.litab5])
		)
	}
  
	callback_healthissues_decider_radios(radio) {    
		if(radio.checked == true) {
			this.data_write('_healthIssues', radio.value);       
			radio.value == 'NO' ? ( this.healthIssues_reset(), this.healthIssues_toggleIssuesPanel(false) ) : this.healthIssues_toggleIssuesPanel(true),

			this.validation_stepFields(this.step4Fields, this.goto5) == true ? (
				this.validation_toggleStepBtn(true, this.goto5), this.validation_toggleTab(false, [this.litab5])
			) : (
				this.validation_toggleStepBtn(false, this.goto5), this.validation_toggleTab(false, [this.litab5])
			)   
		}  
	}

	callback_issues_checkboxes(checkbox, event) {
		checkbox.checked == true ? ( 
			this.healthIssuesArray.push(checkbox.value), 
			this.data_write('_typeOfHealthIssues', this.healthIssuesArray)                           
		) : ( 
			this.healthIssuesArray = this.healthIssuesArray.filter(item => item !== checkbox.value), 
			this.data_write('_typeOfHealthIssues', this.healthIssuesArray) 
		) 

		//this.atLeastOneAbortChecked = (el) => el.value == 'ABORT' && el.checked;
		//[...this.catHealthIssuesFields].some(this.atLeastOneAbortChecked) == true ? this.abortFlag = true : this.abortFlag = false  

		if(event != undefined) {
			this.toggleCheckBoxRequired(this.catHealthIssuesFields); 
			
			// If we're already on step 5, update the filtering immediately
			if (this.querySelector('#food-planner-step-5').style.display !== 'none') {
				this.applyRecipeFilters();
				this.renderRecipeCardsState();
			}
			
			this.validation_stepFields(this.step4Fields, this.goto5) == true ? ( 
				this.validation_toggleStepBtn(true, this.goto5), this.validation_toggleTab(false, [this.litab5])
			) : ( 
				this.validation_toggleStepBtn(false, this.goto5), this.validation_toggleTab(false, [this.litab5]) 
			)
		}   
	}      

  	// ~~~ step 5 ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

	callback_product_checkboxes(checkbox) {

        checkbox.checked == true ? (			
            this.currentCheckedPrdCnt += 1,                  
            this.recipesArray.push(checkbox.value), 
            this.data_write('_recipes', this.recipesArray) 
        ) : (           
            this.currentCheckedPrdCnt -= 1,    
            this.currentCheckedPrdCnt < 0 ? this.currentCheckedPrdCnt = 0 : null,                               
            this.recipesArray = this.recipesArray.filter(item => item !== checkbox.value), 
            this.data_write('_recipes', this.recipesArray) 
        ); 

		// if there is at least one selected recipe, then show the other panels, otherwise hide them
		// The user can't proceed to precheckout(treats)/checkout without selecting at least one recipe, 
		if(this.currentCheckedPrdCnt > 0) {
			this.panels_toggle([ [this.preCheckoutPanel, true],  [this.checkoutPanel, true] ]);
		} else {
			this.panels_toggle([ [this.preCheckoutPanel, false], [this.checkoutPanel, false] ])
		}

	}

  	callback_product_details(e) {
		this.querySelectorAll('.a-product details').forEach(prd => {
			if (!prd.contains(e.target)) {
				prd.open = false;
			}
		});    
	}
  
	/*
		Note the call to products_filterIDS. Each periodicity value corresponds to a different variant.
		Reminder: the variants are stored in main-planner-new.liquid, in GlobalPlannerVariables (this.product_variants_capture)
	*/
	callback_periodicity_radios(radio) {
		if(radio.checked == true) {
			const previousPeriodicity = this.periodicityValue;
			this.data_write('_periodicity', radio.value);
			this.periodicityValue = radio.value;
			this.periodicityDays = parseInt(radio.dataset.days, 10) || (parseInt(radio.value, 10) * 7);
			this.totalRequiredPortions = this.periodicityDays;
			
			// console.log('Periodicity changed. New totalRequiredPortions:', this.totalRequiredPortions);

			if (this.isManualMode) {
				this.toggleManualMode(false); // Exit manual mode due to periodicity change
			}

			this.redistributePortionsAndUpdateDisplay();
			// Update manual mode controls visibility after redistribution
			this.updateManualModeControlsVisibility();
			this.labels_write();
		}
	}

	callback_plantype_radios(radio) {
		if(radio.checked == true) {
			const wasInManualMode = this.isManualMode;
			const previousPlanType = this.planTypeValue;
			
			this.data_write('_typeOfPlan', radio.value);    			
			this.planTypeValue = radio.value;

			// Check if periodicity changed due to plan type change
			const previousPeriodicity = this.periodicityValue;
			const checkedPeriodicityRadio = this.querySelector('[name="PERIODICITY"]:checked');
			if (checkedPeriodicityRadio) {
				this.periodicityValue = checkedPeriodicityRadio.value;
				this.periodicityDays = parseInt(checkedPeriodicityRadio.dataset.days, 10) || (parseInt(checkedPeriodicityRadio.value, 10) * 7);
				this.totalRequiredPortions = this.periodicityDays;
			}

			const periodicityChanged = (previousPeriodicity !== this.periodicityValue);

			/* 
				this.planTypeDescription_s = subscription, 
				this.planTypeDescription_o = one-time purchase
			*/

			if(radio.value == 'SUBSCRIPTION') {
				this.panels_toggle([ [this.planTypeDescription_s, true], [this.planTypeDescription_o, false] ]);
				
				this.querySelector('#period_biweekly').disabled = false;
				this.querySelector('#period_biweekly').classList.remove('border-gray');
				this.querySelector('#period_biweekly').previousElementSibling.classList.remove('not-allowed')
				this.querySelector('#period_biweekly').parentElement.classList.remove('not-allowed', 'opacity-05')
			} else {
				this.panels_toggle([ [this.planTypeDescription_s, false], [this.planTypeDescription_o, true] ])
				this.querySelector('#period_biweekly').disabled = false;
				this.querySelector('#period_biweekly').classList.remove('border-gray');
				this.querySelector('#period_biweekly').previousElementSibling.classList.remove('not-allowed')
				this.querySelector('#period_biweekly').parentElement.classList.remove('not-allowed', 'opacity-05')
			} 

			// If periodicity actually changed, redistribute portions and exit manual mode
			if (periodicityChanged) {
				// console.log('Plan type change triggered periodicity change, redistributing portions');
				if (wasInManualMode) {
					this.toggleManualMode(false); // Exit manual mode due to periodicity change
				}
				this.redistributePortionsAndUpdateDisplay();
				this.updateManualModeControlsVisibility();
			} else {
				// Otherwise, just update prices while preserving current portions AND manual mode state
				// console.log('Plan type change without periodicity change, preserving portions and manual mode state. Current portions:', this.recipePortions);
				this.activeRecipeHandles.forEach(productHandle => {
					// Use the currently stored portions for this recipe
					const currentPortions = this.recipePortions[productHandle] || 0; 
					// console.log(`Preserving ${currentPortions} portions for ${productHandle}`);
					this.updatePortionAndPrice(productHandle, currentPortions); 
				});
				
				// Restore manual mode state if it was enabled and no periodicity change occurred
				if (wasInManualMode && !this.isManualMode) {
					// console.log('Restoring manual mode state after plan type change');
					this.toggleManualMode(true, true); // Restore manual mode, skip redistribution
				}
			}

			this.labels_write();
		}
	}


	/* =================================
	=== Validation related functions === 
	================================= */
	validation_stepFields(fields, nextBtn) {

		/* 
			parameters:
				fields = array of fields to validate
				nextBtn = step button to enable/disable
			For example, in step 1, if the name field is empty, the next button will be disabled
			validity is an attribute on the required fields HTML, read more here: https://developer.mozilla.org/en-US/docs/Web/API/ValidityState
			validity.valueMissing: https://developer.mozilla.org/en-US/docs/Web/API/ValidityState/valueMissing
		*/

		// This array will hold the fields that are invalid
		let invalidFields = [];
		let flagValid = true; 

		/*
			For each field, check if the field is empty and required.
			If yes, add the field to the invalidFields array, apply relevant CSS styling to indicate validation error and set flagValid to false.
			Otherwise, remove the field from the invalidFields array (invalidFields.filter) and apply relevant CSS styling to indicate validation success.
		*/
		fields.forEach((field) => { 
			field.validity.valueMissing == true && field.hasAttribute('required') ? (         
				flagValid = false, 
				this.validation_toggleStyle(false, field.closest('.fieldWrapper')),          
				invalidFields.push(field)           
			) : ( 
				this.validation_toggleStyle(true, field.closest('.fieldWrapper')),
				invalidFields = invalidFields.filter(field => field !== field.validity.valueMissing)
			) 
		}); 

		/*
			After validating all fields, the function checks the value of flagValid.
			If there are invalid fields, disable the next button
			TO-DO: read notes on a11y_updateLiveRegion methods
		*/

		flagValid == true ? this.validation_toggleStepBtn(true, nextBtn) : (this.validation_toggleStepBtn(false, nextBtn), this.a11y_updateLiveRegion(invalidFields, nextBtn) )
		return flagValid;        
	}

	validation_toggleStyle(state, element) {
		state == false ? ( 
			element.classList.remove('validField'), element.classList.add('invalidField') 
		) : ( 
			element.classList.remove('invalidField'), element.classList.add('validField') 
		);      
	}                   


	validation_toggleStepBtn(state, element){
		state == false ? ( 
			element.setAttribute('aria-disabled', true), 
			element.setAttribute('disabled', true), 
			element.classList.add('disabled')
		) : ( 
			element.removeAttribute('aria-disabled'), 
			element.removeAttribute('disabled'), 
			element.classList.remove('disabled') 
		)        
	}    

	validation_toggleTab(state, elements) {
		// Maybe rewrite this using normal if/else statements to be more readable
		elements.forEach((element) => {
			element.tagName == 'LI' && typeof(element) != 'undefined' && element != null && element.querySelector('a') ? 
			state == false ? (
				element.classList.add('disabled'), element.querySelector('a').setAttribute('aria-disabled', true)
			) : (
				element.classList.remove('disabled'), element.querySelector('a').removeAttribute('aria-disabled', true)
			) : null //console.log('tab error')
		})
	}


	/* =========================
	=== Label Text functions === 
	========================= */
	labels_write() { 
							
		// periodicity
		this.labels_Periodicity.forEach(l_periodicity => {
			l_periodicity.innerHTML = this.data_read(['_periodicity']);
		})		

		if(this.data_read(['_periodicity']) == '1') {
			this.labels_week.forEach(l_week => { l_week.style.display = 'initial' });
			this.labels_weeks.forEach(l_weeks => { l_weeks.style.display = 'none' });
		} else {
			this.labels_week.forEach(l_week => { l_week.style.display = 'none' });
			this.labels_weeks.forEach(l_weeks => { l_weeks.style.display = 'initial' });
		}

		// days     
		if(this.data_read(['_periodicity']) == '1') { this.labels_Days.forEach((days) => { days.innerHTML = '7'; this.periodicityDays = 7; }) } 
		if(this.data_read(['_periodicity']) == '2') { this.labels_Days.forEach((days) => { days.innerHTML = '14'; this.periodicityDays = 14; }) } 
		if(this.data_read(['_periodicity']) == '4') { this.labels_Days.forEach((days) => { days.innerHTML = '28'; this.periodicityDays = 28; }) } 

		// Price is now handled by calculateAndUpdateOverallTotal() in the new system
		// The new interactive recipe selection system manages all pricing automatically

		// grams per day
		this.labels_GramsPerDay.forEach((gpd) => { gpd.innerHTML = this.data_read(['_grams_per_day']) });

		// name
		this.labels_Name.forEach((ln) => { ln.innerHTML = this.data_read(['_name']) });

		// gender
		this.data_read(['_gender']) == 'FEMALE' ? (          
			this.labels_Gender.forEach((lg)    => { lg.innerHTML = 'η' }),         
			this.labels_Gender2.forEach((lg2)  => { lg2.innerHTML = 'την' }),        
			this.labels_Adulthood.forEach((la) => { la.innerHTML = 'ενήλικη' })
		) : (         
			this.labels_Gender.forEach((lg)    => { lg.innerHTML = 'o' }),        
			this.labels_Gender2.forEach((lg2)  => { lg2.innerHTML = 'τον' }),        
			this.labels_Adulthood.forEach((la) => { la.innerHTML = 'ενήλικος' })                
		);

		// age
		this.data_read(['_age'])[0] !== null ? (             
			this.data_read(['_adulthood']) == 'ADULT' ? (
			this.label_ageAdult.classList.remove('none'),
			this.label_ageKitten.classList.add('none'),
			this.data_read(['_age']) == '1' ? this.label_ageAdult.textContent = 'έτους' : this.label_ageAdult.textContent = 'ετών'
			) : (
			this.label_ageKitten.classList.remove('none'),
			this.label_ageAdult.classList.add('none')
			)                        
		) : null //console.log('null age');

	}

	/* 
		This is required here because periodicity changes the overall price. 
		This could be refactored, because there is another mutation observer in TreatsManager that also does a similar job.
		However, the periodicity panel is OUTSIDE the TreatsManager custom element, so it's not possible to use the same mutation observer.
		So maybe there should be a single mutation observer that wraps every possible price change source and handles them all appropriately.
		For now, this works, but it's not the best solution.
	*/
	add_totalTotal_MutationObserver() {
			
		let options = { attributes: true };         
		let temp = []
	
		let callback = (mutationList, observer) => {
			for (const mutation of mutationList) {
				if (mutation.type === 'attributes') {
					temp.push(mutation.target) 

					let recipe_total = Number(document.querySelector('.finalTotal').dataset.recipe_total);
					let extras_total = Number(document.querySelector('.finalTotal').dataset.extras_total);           
					let total = (recipe_total + extras_total) / 100;
					document.querySelector('.finalTotal .label_combined_total').textContent = `${new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(total)}`;                      
				}              
			}
		};

		let buldMutationObserver = new MutationObserver(callback);
		buldMutationObserver.observe(document.querySelector('.finalTotal'), options);

	}


	/* ==========
	=== Other === 
	========== */

	goto_page(element) {     
		element.click();  
	}

	// This is used to prevent the user from submitting the form by pressing Enter
	disableFormSubmission() {
		this.form.addEventListener('keydown', (e) => {
			e.key === "Enter" ? e.preventDefault() : null;                      
		})
	}

	/*
		This is used to handle step 4, where the user selects the health issues / allregies of the cat.
		The initial state is that there are no health issues or allergies, so the panels are hidden. (so all checkboxes are unchecked and not required)
		However, if the user selects that the cat has health issues / allergies, then the corresponding panel is shown and the checkboxes are required.
		This is done because the user must select at least one checkbox in each panel.
		If there is at least one checkbox selected, then the rest of the checkboxes are not required.
		Also, there is the following scenario:
			The user checks that the cat has health issues, selects one or more issues but then selects that the cat has no issues.
			In this case, the health issues checkboxes should not be required.
	*/
	toggleCheckBoxRequired(fields) {   
		let atLeastOneChecked = false;
		fields.forEach((f) => { if(f.checked == true) { atLeastOneChecked = true } });      
		if(atLeastOneChecked == true) {
			fields.forEach((f) => { f.removeAttribute('required') });
		} else {
			fields.forEach((f) => { atLeastOneChecked = false; f.setAttribute('required', true); }); 
		}
	}

	grams_per_day_kitten() {
		// Read the age of the kitten (in months)
		let num_age = this.data_read(['_age'])[0];
		// console.log("num_age:",num_age);

		// Find the matched array of the kitten's age
		let matched_month_array = this[`kilogroup_${num_age}`];
		// console.log("matched_month_array:",matched_month_array);
		
		// Read the kilos of the kitten
		let num_kgs = this.data_read(['_weight'])[0];
		// console.log("num_kgs:",num_kgs);
		
	/*
	The findIndex() method is called with a function that returns true when the weight of the kitten matches a weight in the current sub-array (sub.indexOf(num_kgs) !== -1). 
	This means it finds the index of the weight group that matches the kitten's weight.
	*/
	let indexOfRemainingArray = matched_month_array.findIndex(function(sub) {
		return sub.indexOf(num_kgs) !== -1;
	});
	// console.log("indexOfRemainingArray:",indexOfRemainingArray);
	// console.log('grams_per_day_kitten', indexOfRemainingArray, this.fixed_grams[indexOfRemainingArray])
	// console.log("fixed_grams[indexOfRemainingArray]:",this.fixed_grams[indexOfRemainingArray]);
	return this.fixed_grams[indexOfRemainingArray];
	}

	/*
		this is used to show/hide the .fixed-pricing element
		it hides it when the .userRecipesObservable (in preCheckoutPanel) element is on screen
		and shows it when the element is not on screen.
		The switch is done because they display the same information, so there is no need to show both at the same time.
	*/
	fixed_pricing_panel_IO() {
		const observer = new IntersectionObserver(entries => {
			entries.forEach(entry => {
				entry.isIntersecting ? entry.target.classList.add('onScreen') : entry.target.classList.remove('onScreen');		  	
			})
		}, { threshold: 0.25 })	  
		observer.observe(this.querySelector('.userRecipesObservable'))
	}	


	/* =============================
	=== Paneλs related functions ===  
	============================= */ 

	// This is used to toggle the visibility of panels in step 5
	panels_toggle(parameters) {
		parameters.forEach((param) => {
			let element = param[0];
			let elState = param[1];       
			elState == true ? ( 
				element.classList.remove('none'), 
				element.classList.add('flex')
			) : (
				element.classList.remove('flex'), 
				element.classList.add('none')
			);     
		})   
	}

	// This is used to reset all panels in step 5 to display: none
	panels_reset() { 
		[ this.loaderPanel, this.prefacePanel, this.periodicityPanel, 
			this.checkoutPanel, this.productsPanel, this.planTypePanel, 
			this.abortPanel, this.unavailablePanel, this.errorPanel, this.preCheckoutPanel
		].forEach((panel) => { panel.classList.remove('flex'); panel.classList.add('none') })
	}

	/* 
		This is called only once in step 4, when the user clicks the button to proceed to the last step.
		This essentially prepares the panels for step 5, based on the user's selections in the previous steps.
		For example: 
			if the user has selected any allergies, e.g: the cat is allergic to chicken, do not show chicken recipe.
			if the user has selected any critical health issues, e.g: cat has kidney problems (this.abortFlag == true), do not show the recipes but show the abort panel instead
			if the cat requires less than 600grams of food per day, disable the weekly option and select the monthly option
			etc.
		
		FUTURE: Loader could remain for a longer time to emulate a 'thinking' state
	*/
	panels_set_step5() { 
		// Replace old step5 logic with new interactive system
		this.initializeStep5View();
	}  


	/* ==============================
	=== Product related functions === 
	============================== */

	// unchecked all recipe products    
	products_reset() {
		this.products.forEach((prod) => {
			prod.querySelector('input').checked = false;
		});
		this.currentCheckedPrdCnt = 0;
	}    

	/*
		This is used to filter (show/hide in essence) recipes based on the user's selections in the previous steps.
		For example, if the user has selected that the cat is allergic to chicken, then the chicken recipe will be hidden.
		If the user has selected that the cat is a kitten, then the adult recipes will be hidden and the kitten recipes will be shown.
	*/
	products_filter(allergiesArray){
		let allergyType; 
		this.products.forEach(prd => { prd.classList.remove('none') })
		
		// Filter by adulthood            
		if(this.data_read(['_adulthood'])[0] != null) {
		
			if(this.data_read(['_adulthood'])[0] == 'KITTEN') {
				this.products.forEach(prd => {  
					if(prd.dataset.kittens == 'false') {
						prd.classList.add('none');
						prd.querySelector('input').checked = false;
					}     
				})
			}

			if(this.data_read(['_adulthood'])[0] == 'ADULT') {
				this.products.forEach(prd => {  
					if(prd.dataset.adult == 'false') {
						prd.classList.add('none');
						prd.querySelector('input').checked = false;
					}     
				})
			}
		
		}
		
		// Filter by allergy
		if(allergiesArray[0].length > 0) {

			allergiesArray[0].forEach((allergy) => {      
			let temp = allergy.split('_');
			allergyType = temp[0];
			this.querySelectorAll(`.a-product[data-meat="${allergyType}"]`).forEach((prd) => { prd.classList.add('none') })         
			});

			this.querySelectorAll(`.a-product`).length == this.querySelectorAll(`.a-product.none`).length ? this.unavailableFlag = true : this.unavailableFlag = false;       
	
		} else { this.unavailableFlag = false }

	}
  
	/*
    	This function filters entries of variantsArray and returns those that match the condition.
		The condition is that the first element of the entry (Product Title) must match the data-match attribute of the periodicityField.
		Also, the data_read(['grams_per_day']) value must match the variant[2].
		E.g: If the user has selected 'Φαγητό για 4 εβδομάδες' option, then this will return the variant that has the title 'Φαγητό για 4 εβδομάδες' 
		and the grams_per_day value. Check the console.log() to see the filtered array.
		
		DEPRECATED: This method is no longer used in the new interactive recipe selection system.
	*/
	products_filterIDS(periodicityField) {
		console.warn('products_filterIDS is deprecated and no longer used in the new interactive recipe selection system.');
		return [];
	}

	product_CreateFinalProduct(matchedProduct, recipes, name) {

        let attributes = this.cart_DataToObject();  

		// recipe
		var temp;
		var matched_variant_id = matchedProduct[0][1];
		var matched_plan_id = matchedProduct[0][5]; 
		
		/*
			This is where the object that will be sent to Cart API is created.
			Required: id, quantity
			The check for language is done for Checkout.

			Note that that the ELSE statement of: if(planTypeField.checked == true && planTypeField.value == 'ONE_TIME')
			also contains the selling_plan:matched_plan_id. matchedProduct[0][5]
			This is because the selling_plan is required for subscriptions.
		*/
        
		this.planTypeFields.forEach((planTypeField) => {
			if(planTypeField.checked == true && planTypeField.value == 'ONE_TIME') {
				if (document.documentElement.lang == 'el') {
					temp = { id:matched_variant_id, quantity:1, properties: { Συνταγές : recipes.toString(), Pet : name, ...attributes } };
				} else if (document.documentElement.lang == 'en') {
					temp = { id:matched_variant_id, quantity:1, properties: { Recipes : recipes.toString(), Pet : name, ...attributes } };
				} else {
					temp = { id:matched_variant_id, quantity:1, properties: { Recipes : recipes.toString(), Pet : name, ...attributes} };
				}
			} else {
				if (document.documentElement.lang == 'el') {
					temp = { id:matched_variant_id, quantity:1, selling_plan:matched_plan_id, properties: { Συνταγές : recipes.toString(), Pet : name, ...attributes } };
				} else if (document.documentElement.lang == 'en') {
					temp = { id:matched_variant_id, quantity:1, selling_plan:matched_plan_id, properties: { Recipes : recipes.toString(), Pet : name, ...attributes } };
				} else {
					temp = { id:matched_variant_id, quantity:1, selling_plan:matched_plan_id, properties: { Recipes : recipes.toString(), Pet : name, ...attributes } };
				}			
			}
		});

		this.finalProduct = temp;

		/*
			treats
			In case the user has also selected treats, this will create an object for each treat and add it to the finalProductArray.
		*/
		if(typeof(document.querySelector('extras-manager')) != 'undefined' && document.querySelector('extras-manager') != null){
			if(document.querySelector('extras-manager').dataset.hasextras == 'true') {
				this.finalProductArray = [];

				let trts = document.querySelectorAll('product-card.a-treat');				

				trts.forEach((trt) => {     
					if( Number(trt.dataset.item_totalqty) > 0 ) {
						let temp_trt = {id:Number(trt.dataset.item_id), quantity: Number(trt.dataset.item_totalqty)}
						this.finalProductArray.push(temp_trt);
					}
				});

				this.finalProductArray.push(temp);
			}
		}
	}


	/* ==========================
	=== Age related functions === 
	========================== */

	/* 
		this is used to show/hide the age fields based on the user's selection in step 2.
		this is done because cat age is in years, while kitten age is in months.
		Note that since age is required in any case (either kitten or adult), there is a switcheroo of the required attribute.
		Hiding a required field does not mean that it is not required, so the required attribute is switched between the two fields.
	*/
	age_toggleSelectors(element){
		element.checked && element.value == 'KITTEN' ? (
			this.catKittenAgeWrapper.classList.add('flex'),
			this.catKittenAgeWrapper.classList.remove('none'),        
			this.catAdultAgeWrapper.classList.remove('flex'),
			this.catAdultAgeWrapper.classList.add('none'),        
			this.catKittenAgeFields.setAttribute('required', true),
			this.catAdultAgeFields.removeAttribute('required')   
		) : (
			this.catAdultAgeWrapper.classList.add('flex'),
			this.catAdultAgeWrapper.classList.remove('none'),         
			this.catKittenAgeWrapper.classList.remove('flex'),
			this.catKittenAgeWrapper.classList.add('none'),       
			this.catAdultAgeFields.setAttribute('required', true),
			this.catKittenAgeFields.removeAttribute('required') 
		);
	}

	/*
		Complementary function to age_toggleSelectors
		FUTURE: Maybe these two can be combined into a single function
	*/
	age_resetSelector(element) {
		element.checked && element.value == 'KITTEN' ? (
			this.catAdultAgeFields.selectedIndex = 0,
			this.label_ageAdult.classList.add('none')
		) : (
			this.catKittenAgeFields.selectedIndex = 0,
			this.label_ageKitten.classList.add('none')
		)
	}


	/* ================================
	=== Allergies related functions === 
	================================ */
	allergies_reset() {    
		this.allergiesArray = []; 
		this.data_write('_typeOfAllergies', this.allergiesArray);
		this.catAllergiesFields.forEach((f) => {
			f.removeAttribute('required');
			f.checked = false;
			this.allergies_toggleAllergiesTextarea(false);
			this.data_write('_extraAllergies', '');
			this.extraAllergiesField.value = '';
		})    
	}

	allergies_toggleAllergiesPanel(state) {      
		state == false ? (           
			this.catAllergiesWrapper.classList.add('none'), 
			this.catAllergiesWrapper.classList.remove('block')
		) : (
			this.catAllergiesWrapper.classList.add('block'),
			this.catAllergiesWrapper.classList.remove('none'),        
			this.catAllergiesFields.forEach((f)=>{ f.setAttribute('required', true) })    
		)
	}     

	allergies_toggleAllergiesTextarea(state){
		state == false ? (
			this.catExtraAllergiesWrapper.classList.remove('flex'),
			this.catExtraAllergiesWrapper.classList.add('none'),
			this.extraAllergiesField.removeAttribute('required')           
		) : (
			this.catExtraAllergiesWrapper.classList.add('flex'),
			this.catExtraAllergiesWrapper.classList.remove('none'),
			this.extraAllergiesField.setAttribute('required', true)
		);
	}


	/* ====================================
	=== Health issues related functions === 
	==================================== */
	healthIssues_reset() {   
		this.healthIssuesArray = [],
		this.data_write('_typeOfHealthIssues', this.healthIssuesArray)      
		this.catHealthIssuesFields.forEach((f) => { f.removeAttribute('required'); f.checked = false });
		this.abortFlag = false;
	}

	healthIssues_toggleIssuesPanel(state) {
		state == false ? (           
			this.catHealthIssuesWrapper.classList.add('none'), 
			this.catHealthIssuesWrapper.classList.remove('block')
		) : (
			this.catHealthIssuesWrapper.classList.add('block'),
			this.catHealthIssuesWrapper.classList.remove('none'),        
			this.catHealthIssuesFields.forEach((f)=>{ f.setAttribute('required', true) })    
		)   
	}


	/* ====================================
	=== Accessibility related functions === 
	==================================== */
	/* 
		This is not complete and needs REFACTORING / FURTHER DEVELOPMENT.
		The message should include the name of the field that is invalid along with a helpful message.
		Also, why not make the error message visible to all users and not only screen readers?
		The time when the screen reader reads the message is also an issue.
		Should the message be read when the user lands on a step that contains required fields? 
		Should the message be read when the user is on the next step button? (this currently cannot be done because the button is disabled, so the focus cannot reach it)
	*/
	a11y_updateLiveRegion(invalidFields, button) { 
		let region = this.querySelector('#plannerLiveRegion');    
		typeof(region) != 'undefined' && region != null ? region.remove() : null; 
		let clone = this.liveRegionTemplate.content.cloneNode(true);
		button.parentNode.insertBefore(clone, button);
		//this.liveRegionTemplate.parentNode.insertBefore(clone, this.liveRegionTemplate);
		this.querySelector('#plannerLiveRegion').textContent = `Βεβαιωθείτε ότι έχετε συμπληρώσει όλα τα πεδία`;
	}

	a11y_updateActivityRegion(desc) {
		let region = this.querySelector('#activityLiveRegion');
		typeof(region) != 'undefined' && region != null ? region.remove() : null;    
		let clone = this.activityRegionTemplate.content.cloneNode(true);
		this.activityRegionTemplate.parentNode.insertBefore(clone, this.activityRegionTemplate);
		this.querySelector('#activityLiveRegion').textContent = desc;            
	}  


	/* ===========================
	=== Cart related functions === 
	=========================== */  
	cart_clear() {
		const result = fetch("/cart/clear.js", {
			method: "POST",
			headers: {
				"Accept": "application/json"
			}
		});
	}

	async cart_addItems() { 
		console.log('Cart items to add:', this.finalProductArray);

		const data = { items: this.finalProductArray };
		console.log('Request payload:', JSON.stringify(data, null, 2));
		
		try {
			const result = await fetch("/cart/add.json", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"Accept": "application/json"
				},        
				body: JSON.stringify(data)        
			});
			
			if (!result.ok) {
				const errorText = await result.text();
				console.error('Cart add failed:', result.status, errorText);
				
				// Try to parse error as JSON for more details
				try {
					const errorJson = JSON.parse(errorText);
					console.error('Parsed error details:', errorJson);
					if (errorJson.description || errorJson.message) {
						console.error('Error message:', errorJson.description || errorJson.message);
					}
				} catch (e) {
					console.error('Could not parse error as JSON');
				}
				
				return { status: result.status, error: errorText };
			}
			
			console.log('Cart add successful');
			return { status: result.status };
		} catch (error) {
			console.error('Cart add error:', error);
			return { status: 500, error: error.message };
		}
	}

	// async cart_setAttributes(attributes = {}) {    
	// 	const result = await fetch("/cart/update.json", {
	// 		method: "POST",
	// 		headers: {
	// 			"Content-Type": "application/json",
	// 			"Accept": "application/json"
	// 		},
	// 		body: JSON.stringify({ 
	// 			attributes: attributes
	// 			// note: ''+ this.querySelector('#Cart-note').value +''
	// 		})
	// 	});    
	// 	return result;
	// }  

	cart_DataToObject() {    
		const tempObj = {};
		this.cat.forEach((entry) => {         
			for (const property in entry) {        
				let key = property;
				let value = entry[property];        
				tempObj[`${key}`] = `${value}`; // Ensure all values are strings
			}  
		});   
		return tempObj;     
	}

	// New methods for interactive recipe selection (cat version)
	formatMoney(cents, format) {
		if (typeof cents === 'string') {
			cents = parseInt(cents, 10);
		}
		
		const value = cents / 100;
		return new Intl.NumberFormat('el-GR', {
			style: 'currency',
			currency: 'EUR',
			minimumFractionDigits: 2,
			maximumFractionDigits: 2
		}).format(value);
	}

	updatePortionAndPrice(productHandle, newPortionValue, isPreview = false) {
		// console.log(`[DEBUG] updatePortionAndPrice CALLED for: ${productHandle}, Portions: ${newPortionValue}, Preview: ${isPreview}, Current Plan: ${this.planTypeValue}, Current Periodicity: ${this.periodicityValue}`);

		if (!this.recipeData || !this.recipeData[productHandle] || !this.recipeData[productHandle].variants) {
			// console.log(`[DEBUG] No recipe data found for ${productHandle}`);
			return;
		}

		// Store the portions if not in preview mode
		if (!isPreview) {
			this.recipePortions[productHandle] = newPortionValue;
		}

		// Get variant key like "4-ebdomades_300gr-mera"
		const gramsPerDay = this.data_read(['_grams_per_day']);
		const variantKey = `${this.periodicityValue}-ebdomades_${gramsPerDay}gr-mera`;
		const variantData = this.recipeData[productHandle].variants[variantKey];

		if (!variantData) {
			// console.log(`[DEBUG] No variant data found for ${productHandle} with key ${variantKey}`);
			return;
		}

		// Update UI elements
		const card = this.querySelector(`.a-product[data-product-handle="${productHandle}"]`);
		if (card) {
			const displaySpan = card.querySelector('.recipe-portion-display');
			const input = card.querySelector('.recipe-portion-input');
			const priceDisplay = card.querySelector('.recipe-price-display');

			if (displaySpan) displaySpan.textContent = newPortionValue;
			if (input) input.value = newPortionValue;

			// Calculate price based on plan type
			let pricePerPortion;
			if (this.planTypeValue === 'SUBSCRIPTION') {
				pricePerPortion = variantData.discountedPrice || variantData.price;
			} else {
				pricePerPortion = variantData.price;
			}
			
			const totalPrice = pricePerPortion * newPortionValue;
			if (priceDisplay) {
				priceDisplay.textContent = this.formatMoney(totalPrice);
			}
		}

		// Update overall totals
		this.calculateAndUpdateOverallTotal();
	}

	calculateAndUpdateOverallTotal() {
		let overallRecipeTotalCents = 0;
		let currentTotalSelectedPortions = 0;

		this.activeRecipeHandles.forEach(productHandle => {
			const portions = this.recipePortions[productHandle] || 0;
			currentTotalSelectedPortions += portions;

			const gramsPerDay = this.data_read(['_grams_per_day']);
			const variantKey = `${this.periodicityValue}-ebdomades_${gramsPerDay}gr-mera`;
			const variantData = this.recipeData[productHandle]?.variants?.[variantKey];
			
			if (variantData) {
				let pricePerPortion;
				if (this.planTypeValue === 'SUBSCRIPTION') {
					pricePerPortion = variantData.discountedPrice || variantData.price;
				} else {
					pricePerPortion = variantData.price;
				}
				overallRecipeTotalCents += pricePerPortion * portions;
			}
		});

		// console.log('Total Selected Portions:', currentTotalSelectedPortions, 'Required:', this.totalRequiredPortions);
		// console.log('Overall Recipe Total Cents:', overallRecipeTotalCents);

		// Update data attributes for the final total calculation
		if (this.final_total_wrapper) {
			this.final_total_wrapper.dataset.recipe_total = overallRecipeTotalCents;
		}

		// Update pricing labels
		const pricePerDay = overallRecipeTotalCents / this.periodicityDays;
		this.labels_Price_per_day.forEach(label => {
			label.textContent = this.formatMoney(pricePerDay);
		});

		this.labels_Price.forEach(label => {
			label.textContent = this.formatMoney(overallRecipeTotalCents);
		});

		// Determine if all required portions are selected and update ATC button
		this.checkPortionTotalAndToggleATC();
	}

	checkPortionTotalAndToggleATC() {
		const currentTotal = Object.values(this.recipePortions).reduce((sum, portions) => sum + (portions || 0), 0);
		const atcButton = this.querySelector('.atcBtn');
		
		if (atcButton) {
			// Enable ATC only if we have the correct total portions AND at least one recipe selected
			if (currentTotal === this.totalRequiredPortions && this.activeRecipeHandles.length > 0) {
				atcButton.disabled = false;
				atcButton.classList.remove('disabled');
			} else {
				atcButton.disabled = true;
				atcButton.classList.add('disabled');
			}
		}
	}

	checkManualTotalAndToggleATC() {
		if (!this.isManualMode) return;
	
		let currentTotal = Object.values(this.recipePortions).reduce((sum, portions) => sum + (portions || 0), 0);
	
		// Update the numbers in the spans
		const statusDiv = this.manualModeStatus;
		if (statusDiv) {
			const currentSpan = statusDiv.querySelector('.manual-mode-current');
			const totalSpan = statusDiv.querySelector('.manual-mode-total');
			if (currentSpan) currentSpan.innerText = currentTotal;
			if (totalSpan) totalSpan.innerText = this.totalRequiredPortions;
		}
	
		if (currentTotal === this.totalRequiredPortions) {
			this.validation_toggleStepBtn(true, this.atc);
			this.manualModeStatus.style.color = 'green'; // Simple styling
			// Show checkout panels if they are hidden and recipes are active
			if(this.activeRecipeHandles.length > 0) {
				this.panels_toggle([ [this.preCheckoutPanel, true], [this.checkoutPanel, true] ]);
			}
		} else {
			this.validation_toggleStepBtn(false, this.atc);
			this.manualModeStatus.style.color = 'red'; // Simple styling
		}

		// Disable/enable plus buttons based on the total
		this.activeRecipeHandles.forEach(handle => {
			const card = this.querySelector(`.a-product[data-product-handle="${handle}"]`);
			if (card) {
				const plusButton = card.querySelector('.button--plus');
				if (plusButton) {
					plusButton.disabled = (currentTotal >= this.totalRequiredPortions);
				}
			}
		});
	}

	applyRecipeFilters() {
		// console.log("Applying unified recipe filters...");
		
		// Reset all products to visible first
		this.products.forEach(product => {
			product.classList.remove('none');
		});

		const allergiesArray = this.data_read(['_typeOfAllergies'])[0] || [];
		const healthIssues = this.data_read(['_typeOfHealthIssues'])[0] || [];
		const adulthood = this.data_read(['_adulthood'])[0];

		// console.log("Filter criteria:", { allergiesArray, healthIssues, adulthood });

		// If no recipe data available, just show all products
		if (!this.recipeData || Object.keys(this.recipeData).length === 0) {
			// console.warn("No recipe data available, showing all products");
			this.unavailableFlag = false;
			return;
		}

		// Filter each product based on its data attributes (similar to dog planner)
		const visibleProducts = [];
		this.products.forEach(product => {
			const productHandle = product.dataset.productHandle;
			const meatType = product.dataset.meat; // e.g., "CHICKEN", "FISH"
			// console.log(`Checking product ${productHandle} with meat type: ${meatType}`);

			let shouldHide = false;

			// Age-based filtering - check if recipe data is available for this check
			if (adulthood && this.recipeData && this.recipeData[productHandle]) {
				const recipeData = this.recipeData[productHandle];
				// console.log(`Recipe data for ${productHandle}:`, { forPuppies: recipeData.forPuppies, forAdults: recipeData.forAdults });
				if (adulthood === 'KITTEN' && !recipeData.forPuppies) { // Note: using 'forPuppies' as it maps to kittens
					shouldHide = true;
					// console.log(`Hiding non-kitten recipe for kitten: ${productHandle}`);
				} else if (adulthood === 'ADULT' && !recipeData.forAdults) {
					shouldHide = true;
					// console.log(`Hiding non-adult recipe for adult: ${productHandle}`);
				}
			}

			// Allergy-based filtering (using data-meat attribute like dog planner)
			if (Array.isArray(allergiesArray) && allergiesArray.length > 0 && meatType) {
				for (const allergen of allergiesArray) {
					// Convert "CHICKEN_ALLERGY" to "CHICKEN" by removing "_ALLERGY" suffix
					const allergenType = allergen.replace('_ALLERGY', '');
					// console.log(`Comparing allergen ${allergenType} with meat type ${meatType}`);
					if (meatType === allergenType) {
						shouldHide = true;
						// console.log(`Hiding ${allergenType} recipe due to allergy: ${productHandle}`);
						break;
					}
				}
			}

			if (shouldHide) {
				product.classList.add('none');
			} else {
				visibleProducts.push(product);
			}
		});

		// Check if no products are available
		if (visibleProducts.length === 0) {
			this.unavailableFlag = true;
		} else {
			this.unavailableFlag = false;
		}

		// console.log(`Filtering complete. Visible products: ${visibleProducts.length}, Unavailable flag: ${this.unavailableFlag}`);
	}

	initializeStep5View() {
		// console.log("Initializing Step 5 View...");
		this.activeRecipeHandles = []; // Reset active recipes
		this.recipePortions = {};      // Reset current portions for all recipes

		// Calculate kitten grams if needed
		if(this.data_read(['_adulthood']) == 'KITTEN') { 
			this.data_write('_grams_per_day', this.grams_per_day_kitten());
		}

		// Get current periodicity and calculate total required portions
		const checkedPeriodicityRadio = this.querySelector('[name="PERIODICITY"]:checked');
		if (checkedPeriodicityRadio) {
			this.periodicityValue = checkedPeriodicityRadio.value;
			this.periodicityDays = parseInt(checkedPeriodicityRadio.dataset.days, 10) || (parseInt(checkedPeriodicityRadio.value, 10) * 7);
			this.totalRequiredPortions = this.periodicityDays;
			// console.log("Initial totalRequiredPortions set to:", this.totalRequiredPortions);
		} else {
			this.periodicityValue = '4'; 
			this.periodicityDays = 28; 
			this.totalRequiredPortions = 28; 
			// console.warn("No periodicity radio found, defaulting totalRequiredPortions.");
		}
		
		// Get current plan type
		const checkedPlanTypeRadio = this.querySelector('[name="TYPE_OF_PLAN"]:checked');
		if (checkedPlanTypeRadio) {
			this.planTypeValue = checkedPlanTypeRadio.value;
		}

		// Apply filters to determine which recipes are available
		this.applyRecipeFilters();

		// Check for unavailable or abort conditions
		if (this.abortFlag === true) {
			this.panels_toggle([[this.abortPanel, true], [this.prefacePanel, false], [this.periodicityPanel, false], 
				[this.preCheckoutPanel, false], [this.productsPanel, false], [this.unavailablePanel, false], 
				[this.checkoutPanel, false], [this.loaderPanel, false]]);
			return;
		}

		if (this.unavailableFlag === true) {
			this.panels_toggle([[this.unavailablePanel, true], [this.abortPanel, false], [this.prefacePanel, false], 
				[this.periodicityPanel, false], [this.preCheckoutPanel, false], [this.productsPanel, false], 
				[this.checkoutPanel, false], [this.loaderPanel, false]]);
			return;
		}

		// For cats, there's no special renal case like dogs, so just proceed with normal flow
		this.renderRecipeCardsState(); // This will also call updateManualModeControlsVisibility()

		// Set up manual mode toggle
		if (this.manualModeToggle) {
			this.manualModeToggle.addEventListener('change', () => {
				this.toggleManualMode();
			});
		}

		// Add/Re-add listeners
		this.products = this.querySelectorAll('.a-product'); // Refresh the NodeList
		// console.log('Attaching listeners to product cards. Count:', this.products.length);
		if (!this.boundHandleRecipeCardClick) { // Bind only once
		    this.boundHandleRecipeCardClick = this.handleRecipeCardClick.bind(this);
		}

		this.products.forEach(productCard => {
			// Remove existing listener to avoid duplicates
			productCard.removeEventListener('click', this.boundHandleRecipeCardClick);
			productCard.addEventListener('click', this.boundHandleRecipeCardClick);
		});
		// console.log("Recipe card click listeners cycle complete.");

		this.panels_toggle([
			[this.prefacePanel, true], [this.productsPanel, true], [this.planTypePanel, true], [this.periodicityPanel, true],
			[this.loaderPanel, false], [this.errorPanel, false], [this.abortPanel, false], [this.unavailablePanel, false]
			// Note: preCheckoutPanel and checkoutPanel visibility is handled by updateCheckoutPanelsVisibility()
		]);

		// Initial setup of quantity controls visibility (should be hidden by default since not in manual mode)
		this.updateQuantityControlsVisibility();

		this.add_totalTotal_MutationObserver();
		this.labels_write();
	}

	renderRecipeCardsState() {
		// console.log("Rendering recipe card states. Active: ", this.activeRecipeHandles, "Manual Mode:", this.isManualMode);
		const visibleProducts = this.querySelectorAll('.a-product:not(.none)'); // Get all products not hidden by filters
		
		visibleProducts.forEach(product => {
			const productHandle = product.dataset.productHandle;
			const quantityControls = product.querySelector('.recipe-quantity-controls');
			
			// Remove all state classes
			product.classList.remove('recipe-selectable', 'recipe-active', 'recipe-disabled', 'recipe-inactive');
			
			// Check if product is inactive (sold out via tag)
			const isInactive = product.dataset.active === 'false';
			if (isInactive) {
				product.classList.add('recipe-inactive');
				if (quantityControls) quantityControls.style.display = 'none';
				// Ensure inactive products are not in active handles
				const inactiveIndex = this.activeRecipeHandles.indexOf(productHandle);
				if (inactiveIndex > -1) {
					this.activeRecipeHandles.splice(inactiveIndex, 1);
					delete this.recipePortions[productHandle];
				}
				return; // Skip further processing for inactive cards
			}
			
			if (this.activeRecipeHandles.includes(productHandle)) {
				// Recipe is active
				product.classList.add('recipe-active');
			} else if (this.activeRecipeHandles.length < this.maxRecipesAllowed) {
				// Recipe can be selected
				product.classList.add('recipe-selectable');
			} else {
				// Maximum recipes reached, this recipe is disabled
				product.classList.add('recipe-disabled');
			}
		});

		// Show/hide manual mode controls based on number of active recipes
		this.updateManualModeControlsVisibility();
	}

	handleRecipeCardClick(event) {
		// Prevent card click logic if click is on quantity controls
		if (event.target.closest('.recipe-quantity-controls')) {
			// console.log("Click on quantity controls, preventing card toggle.");
			return;
		}

		// Prevent card click logic if click is on details/summary
		if (event.target.closest('details') || event.target.closest('summary')) {
			return;
		}

		const productCard = event.currentTarget;
		const productHandle = productCard.dataset.productHandle;

		// Check if the product is inactive (sold out) - prevent selection
		if (productCard.dataset.active === 'false' || productCard.classList.contains('recipe-inactive')) {
			console.log("Product is inactive/sold out, cannot select:", productHandle);
			return;
		}

		// Exit manual mode when any card is clicked (unless already at max recipes and clicking a non-active card)
		if (this.isManualMode) {
			const index = this.activeRecipeHandles.indexOf(productHandle);
			if (index > -1 || this.activeRecipeHandles.length < this.maxRecipesAllowed) {
				this.toggleManualMode(false); // Exit manual mode
			}
		}

		// console.log("Card clicked:", productHandle);

		if (!productHandle) return;

		// Toggle recipe activation
		const index = this.activeRecipeHandles.indexOf(productHandle);
		if (index > -1) { // Recipe is currently active, so deactivate it
			this.activeRecipeHandles.splice(index, 1);
			// console.log("Deactivated:", productHandle, "Active now:", this.activeRecipeHandles);
		} else { // Recipe is not active, try to activate it
			if (this.activeRecipeHandles.length < this.maxRecipesAllowed) {
				this.activeRecipeHandles.push(productHandle);
				// console.log("Activated:", productHandle, "Active now:", this.activeRecipeHandles);
			} else {
				// If we're already at max recipes, do nothing to the recipe list.
				// The click will still serve to exit manual mode if applicable.
				// console.log("Max recipes already selected. Cannot activate:", productHandle);
			}
		}

		// Always re-render state and redistribute portions after any change
		this.renderRecipeCardsState();
		this.redistributePortionsAndUpdateDisplay();
	}

	redistributePortionsAndUpdateDisplay() {
		// console.log("Redistributing portions. Active recipes:", this.activeRecipeHandles, "Total required portions:", this.totalRequiredPortions);

		// Zero out portions for any recipe that was active but is no longer
		const previousActiveHandles = Object.keys(this.recipePortions).filter(handle => this.recipePortions[handle] > 0);
		previousActiveHandles.forEach(handle => {
			if (!this.activeRecipeHandles.includes(handle)) {
				// console.log(`Deactivating and zeroing portions for ${handle}`);
				const card = this.querySelector(`.a-product[data-product-handle="${handle}"]`);
				if (card) {
					this.updatePortionAndPrice(handle, 0);
				}
			}
		});

		const N = this.activeRecipeHandles.length;
		
		if (N === 0) {
			// console.log("No active recipes. Ensuring totals are zero.");
			this.calculateAndUpdateOverallTotal();
			this.checkPortionTotalAndToggleATC();
			return;
		}

		// For cats, use simple even distribution (no complex matrix like dogs)
		const basePortions = Math.floor(this.totalRequiredPortions / N);
		let remainderPortions = this.totalRequiredPortions % N;

		// console.log(`Distributing ${this.totalRequiredPortions} portions among ${N} recipes. Base: ${basePortions}, Remainder: ${remainderPortions}`);

		this.activeRecipeHandles.forEach((productHandle, index) => {
			let assignedPortions = basePortions;
			if (index < remainderPortions) {
				assignedPortions += 1;
			}

			// console.log(`Assigning ${assignedPortions} to ${productHandle}`);
			const card = this.querySelector(`.a-product[data-product-handle="${productHandle}"]`);
			if (card) {
				this.updatePortionAndPrice(productHandle, assignedPortions);
			}
		});

		this.calculateAndUpdateOverallTotal();
		this.checkPortionTotalAndToggleATC();
	}

	updateManualModeControlsVisibility() {
		// Only show manual mode controls when more than one recipe is selected
		if (this.customPortionControls) {
			if (this.activeRecipeHandles.length > 1) {
				this.customPortionControls.classList.remove('is-hidden');
			} else {
				this.customPortionControls.classList.add('is-hidden');
				// If we're hiding the controls and were in manual mode, exit manual mode
				if (this.isManualMode) {
					this.toggleManualMode(false, true); // Exit manual mode, skip redistribution
				}
			}
		}
		
		// Show/hide checkout panels based on recipe selection
		this.updateCheckoutPanelsVisibility();
	}

	updateCheckoutPanelsVisibility() {
		// Only show checkout panels when at least one recipe is selected
		const hasSelectedRecipes = this.activeRecipeHandles.length > 0;
		
		if (this.preCheckoutPanel) {
			if (hasSelectedRecipes) {
				this.preCheckoutPanel.classList.remove('none');
			} else {
				this.preCheckoutPanel.classList.add('none');
			}
		}
		
		if (this.checkoutPanel) {
			if (hasSelectedRecipes) {
				this.checkoutPanel.classList.remove('none');
			} else {
				this.checkoutPanel.classList.add('none');
			}
		}
	}

	toggleManualMode(forceState = null, skipRedistribution = false) {
		// For cats, we don't have renal restrictions like dogs
		
		if (forceState !== null) {
			this.isManualMode = forceState;
		} else {
			this.isManualMode = !this.isManualMode;
		}

		// Update toggle UI
		if (this.manualModeToggle) {
			this.manualModeToggle.checked = this.isManualMode;
		}

		// Update status message
		if (this.manualModeStatus) {
			this.manualModeStatus.style.display = this.isManualMode ? 'block' : 'none';
		}

		// Show/hide quantity selector buttons based on manual mode
		this.updateQuantityControlsVisibility();

		if (this.isManualMode) {
			// Switched TO manual mode
			this.checkManualTotalAndToggleATC(); // Initial check
		} else {
			// Switched TO automatic mode
			// Re-enable ATC because auto mode always has correct totals
			this.validation_toggleStepBtn(true, this.atc);
		}

		// If entering manual mode and not skipping redistribution, start fresh
		if (this.isManualMode && !skipRedistribution) {
			this.redistributePortionsAndUpdateDisplay();
		}

		// console.log(`Manual mode ${this.isManualMode ? 'enabled' : 'disabled'}`);
	}

	updateQuantityControlsVisibility() {
		// Show/hide quantity selector buttons based on manual mode
		this.products.forEach(product => {
			const quantityControls = product.querySelector('.recipe-quantity-controls');
			if (quantityControls) {
				const buttons = quantityControls.querySelectorAll('.button--minus, .button--plus');
				buttons.forEach(button => {
					if (this.isManualMode) {
						button.style.display = 'block';
					} else {
						button.style.display = 'none';
					}
				});
			}
		});
	}


}
customElements.define('food-planner-wizard', FoodPlannerWizard);


/*
	Sets up a MutationObserver to monitor changes to the aria-selected attribute of the tabs.
	When a tab is selected (aria-selected="true"), it triggers a custom tracking event.	
*/
function fbEvents() {

	const targetNodes = document.querySelectorAll(".aTab");
	const config = { attributes: true };
	const callback = (mutationList, observer) => {
		for (const mutation of mutationList) {
			if (mutation.type === "attributes" && mutation.attributeName == 'aria-selected' && mutation.target.getAttribute('aria-selected') == 'true') {
				fbq('trackCustom', 'TabSelected', { tab_id: `${mutation.target.id}` });
			}
		}
	};

	const observer = new MutationObserver(callback);
	targetNodes.forEach(targetNode => {
		observer.observe(targetNode, config);
	})

}


/*
	When the window loads, immediately trigger a custom tracking event for tab1.
	Then call the fbEvents() function to set up the MutationObserver for further tracking events.
*/
window.addEventListener('load', ()=> {
	fbq('trackCustom', 'TabSelected', { tab_id: 'tab1' });
	fbEvents()
})
